import { hasPrecinctBoundaryBreak, scopePrecinctPairKey, BOUNDARY_CHANGE_NOTICE } from "./precinctComparison";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { AttributionControl, GeoJSON, MapContainer, Pane, Polygon, TileLayer, useMap, useMapEvents } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { withCartoBasemapKey } from "./cartoBasemap";
import {
  ELECTION_MAP_STANDARD_V1,
  ELECTION_MAP_STANDARD_V1_KEY,
  getElectionMapFeatureStyle,
  getMapTileUrlsForVariant,
  getOutsideAreaMaskPositions,
  isElectionMapStandardV1,
} from "./mapRenderingStandard";
import { APP_PAGE_BACKGROUND, BRAND_COLORS, BRAND_MAP_COLOR_PRESETS, normalizeHexColor } from "./branding";
import { getElectionLabel, sortElectionsNewestFirst } from "./electionOrdering";
import {
  DEFAULT_MAP_VIEW,
  clearStoredViewport,
  getInitialMapView,
  makeAutoFitRequestKey,
  normalizeViewport,
  readStoredViewport,
  writeStoredViewport,
} from "./mapViewport";
import {
  getStoredChoiceForElection,
  pickValidElectionId,
  pickValidChoice,
  readElectionSelectionContext,
  setStoredChoiceForElection,
  writeElectionSelectionContext,
} from "./electionSelectionContext";
import { getPrimaryEntityLabel, parseAppElectionId } from "./electionBehavior";
import AddToReportButton from "./reports/components/AddToReportButton";
import { buildDefaultReportTitle, captureLeafletViewport, mapLegendToSnapshot } from "./reports/capture";
import { REPORT_ITEM_TYPES, REPORT_ORIENTATIONS, REPORT_SOURCE_MODULES } from "./reports/reportTypes";

const WIDE_LAYOUT_BREAKPOINT = 1100;
const FIXED_CLASS_COUNT = 4;
const DEFAULT_FIXED_BINS = [0, 25, 50, 75, 100];
const NO_DATA_FILL_COLOR = "#e2e8f0";

// CARTO basemap (nice street detail)
const CARTO_BASE_URL =
  "https://{s}.basemaps.cartocdn.com/light_nolabels/{z}/{x}/{y}{r}.png";
const CARTO_LABELS_URL =
  "https://{s}.basemaps.cartocdn.com/light_only_labels/{z}/{x}/{y}{r}.png";
const CARTO_ATTR =
  '<a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> | <a href="https://carto.com/attributions">CARTO</a>';
const ATTRIBUTION_PREFIX =
  'Zdroj: <a href="https://csu.gov.cz/">ČSÚ</a>, <a href="https://geoportal.cuzk.gov.cz/">ČÚZK</a> | <a href="https://leafletjs.com">Leaflet</a>';

const STORAGE_KEY = "electionmap.mapSettings.v2";

export const COLOR_PRESETS = BRAND_MAP_COLOR_PRESETS;

// --- small color helpers (no extra deps) ---
export function clamp01(x) {
  return Math.max(0, Math.min(1, x));
}

export function percentile(values, p) {
  const arr = (Array.isArray(values) ? values : [])
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v))
    .sort((a, b) => a - b);

  if (arr.length === 0) return null;
  const pp = Math.max(0, Math.min(100, Number(p)));
  const idx = (pp / 100) * (arr.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  if (lo === hi) return arr[lo];
  const t = idx - lo;
  return arr[lo] * (1 - t) + arr[hi] * t;
}

export function computeAutoBins(valuesPct, classCount, clipLow = 2, clipHigh = 98) {
  const raw = (Array.isArray(valuesPct) ? valuesPct : [])
    .map((v) => Number(v))
    .filter((v) => Number.isFinite(v));

  const k = Number(classCount);
  const pctList =
    k === 4 ? [25, 50, 75] :
    k === 3 ? [33, 66] :
    k === 5 ? [20, 40, 60, 80] :
    k === 7 ? [14, 28, 42, 56, 70, 84] :
    null;

  if (!pctList) return [0, 100];
  if (raw.length < k) return k === FIXED_CLASS_COUNT ? DEFAULT_FIXED_BINS : [0, 100];

  const loBound = percentile(raw, clipLow);
  const hiBound = percentile(raw, clipHigh);
  if (loBound == null || hiBound == null) return k === FIXED_CLASS_COUNT ? DEFAULT_FIXED_BINS : [0, 100];

  let clipped = raw.filter((v) => v >= loBound && v <= hiBound);
  if (clipped.length < k) clipped = raw;
  if (clipped.length < k) return k === FIXED_CLASS_COUNT ? DEFAULT_FIXED_BINS : [0, 100];

  const thresholds = pctList
    .map((pp) => percentile(clipped, pp))
    .filter((v) => v != null)
    .map((v) => Math.max(0, Math.min(100, Number(v))));

  if (thresholds.length !== pctList.length) return k === FIXED_CLASS_COUNT ? DEFAULT_FIXED_BINS : [0, 100];

  const bins = [0, ...thresholds, 100];

  // Strictly increasing bins; collapse can happen with ties/small samples.
  for (let i = 1; i < bins.length; i++) {
    if (!(bins[i] > bins[i - 1])) return k === FIXED_CLASS_COUNT ? DEFAULT_FIXED_BINS : [0, 100];
  }

  return bins;
}

export function formatBin(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "";
  // Display-only rounding; internal bins remain full precision numbers.
  const s = (Math.round(x * 10) / 10).toFixed(1);
  return s.endsWith(".0") ? s.slice(0, -2) : s;
}

export function formatBinRange(from, to) {
  return `${formatBin(from)}–${formatBin(to)}%`;
}

function hexToRgb(hex) {
  const h = String(hex || "").replace("#", "").trim();
  const full = h.length === 3 ? h.split("").map((c) => c + c).join("") : h;
  const n = parseInt(full, 16);
  return {
    r: (n >> 16) & 255,
    g: (n >> 8) & 255,
    b: n & 255,
  };
}

function rgbToHex({ r, g, b }) {
  const to2 = (v) => v.toString(16).padStart(2, "0");
  return `#${to2(r)}${to2(g)}${to2(b)}`;
}

// mix baseColor -> white by t (0 = base, 1 = white)
export function tintToWhite(baseHex, t) {
  const base = hexToRgb(baseHex);
  const tt = clamp01(t);
  const mixed = {
    r: Math.round(base.r + (255 - base.r) * tt),
    g: Math.round(base.g + (255 - base.g) * tt),
    b: Math.round(base.b + (255 - base.b) * tt),
  };
  return rgbToHex(mixed);
}

export function mixHex(baseHex, targetHex, t) {
  const base = hexToRgb(baseHex);
  const target = hexToRgb(targetHex);
  const tt = clamp01(t);
  return rgbToHex({
    r: Math.round(base.r + (target.r - base.r) * tt),
    g: Math.round(base.g + (target.g - base.g) * tt),
    b: Math.round(base.b + (target.b - base.b) * tt),
  });
}

export function getPaletteSteps(baseHex) {
  const normalizedBase = normalizeHexColor(baseHex);
  const preset = COLOR_PRESETS.find((item) => item.hex.toLowerCase() === normalizedBase);
  if (preset?.steps?.length === FIXED_CLASS_COUNT) {
    return preset.steps;
  }

  const fallbackBase = normalizedBase || BRAND_COLORS.primaryDarkBlue;
  return [
    mixHex(fallbackBase, "#ffffff", 0.82),
    mixHex(fallbackBase, "#ffffff", 0.54),
    mixHex(fallbackBase, "#ffffff", 0.12),
    mixHex(fallbackBase, "#0f172a", 0.38),
  ];
}

function getStoredPresetColor(value, fallbackHex) {
  const normalizedValue = normalizeHexColor(value);
  const preset = COLOR_PRESETS.find((item) => item.hex.toLowerCase() === normalizedValue);
  return preset ? preset.hex : normalizedValue || fallbackHex;
}

export function getBinIndex(value, bins) {
  // bins = [0,5,10,...,100] => returns i where bins[i] <= value < bins[i+1]
  if (value == null || Number.isNaN(value)) return -1;
  for (let i = 0; i < bins.length - 1; i++) {
    if (value >= bins[i] && value < bins[i + 1]) return i;
  }
  return bins.length - 2;
}

export function parseBins(text) {
  const parts = String(text || "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((s) => Number(s))
    .filter((n) => Number.isFinite(n));

  const uniq = Array.from(new Set(parts)).sort((a, b) => a - b);
  if (uniq.length < 2) return [0, 100];
  return uniq;
}

function createHttpError(status, label) {
  const error = new Error(`${label} HTTP ${status}`);
  error.status = Number(status);
  return error;
}

export function isDemoApiBase(apiBase) {
  return String(apiBase || "").startsWith("/api/demo/");
}

export function buildAreaElectionsUrl(areaId, apiBase = "/api") {
  const base = String(apiBase || "/api").replace(/\/+$/, "");
  if (isDemoApiBase(base)) return `${base}/elections`;
  return `${base}/areas/${encodeURIComponent(String(areaId))}/elections`;
}

export function buildAreaDataUrl(resourcePath, areaId, electionId, apiBase = "/api") {
  const base = String(apiBase || "/api").replace(/\/+$/, "");
  const path = String(resourcePath || "").replace(/^\/+/, "");
  const params = new URLSearchParams();
  if (!isDemoApiBase(base)) params.set("area_id", String(areaId));
  params.set("election_id", String(electionId));
  return `${base}/${path}?${params.toString()}`;
}

export function buildAreaArtifactUrl(resourcePath, areaId, apiBase = "/api", extraParams = {}) {
  const base = String(apiBase || "/api").replace(/\/+$/, "");
  const path = String(resourcePath || "").replace(/^\/+/, "");
  const params = new URLSearchParams();
  if (!isDemoApiBase(base)) params.set("area_id", String(areaId));
  Object.entries(extraParams || {}).forEach(([key, value]) => {
    if (value != null && String(value) !== "") params.set(key, String(value));
  });
  const query = params.toString();
  return query ? `${base}/${path}?${query}` : `${base}/${path}`;
}

export function buildApiFetchOptions(apiBase = "/api", options = {}) {
  return {
    ...options,
    credentials: isDemoApiBase(apiBase) ? "omit" : "include",
  };
}

export function getOkrsekTail(okrsek) {
  const raw = String(okrsek ?? "").trim();
  if (!raw) return "";
  const lastColon = raw.lastIndexOf(":");
  return lastColon >= 0 ? raw.slice(lastColon + 1) : raw;
}

const SEN_MUNICIPALITY_NAME_BY_AREA_CSU_CODE = new Map([
  [500224, "Praha 10"],
  [538205, "Praha-Dubeč"],
  [545970, "Plzeň 1"],
  [546003, "Plzeň 3"],
  [546062, "Pesvice"],
  [546071, "Račetice"],
  [546160, "Nezabylice"],
  [546208, "Plzeň 4"],
  [546518, "Loučná pod Klínovcem"],
  [547409, "Praha-Štěrboholy"],
  [554731, "Plzeň 5-Křimice"],
  [554758, "Plzeň 6-Litice"],
  [554766, "Plzeň 7-Radčice"],
  [554774, "Plzeň 8-Černice"],
  [557978, "Plzeň 10-Lhota"],
  [559199, "Plzeň 9-Malesice"],
  [562971, "Chomutov"],
  [562980, "Bílence"],
  [562998, "Blatno"],
  [563005, "Boleboř"],
  [563013, "Březno"],
  [563021, "Černovice"],
  [563048, "Domašín"],
  [563056, "Droužkovice"],
  [563064, "Hora Svatého Šebestiána"],
  [563072, "Hrušovany"],
  [563081, "Chbany"],
  [563099, "Jirkov"],
  [563102, "Kadaň"],
  [563111, "Kalek"],
  [563129, "Klášterec nad Ohří"],
  [563137, "Kovářská"],
  [563161, "Křimov"],
  [563188, "Libědice"],
  [563200, "Málkov"],
  [563218, "Mašťov"],
  [563226, "Měděnec"],
  [563242, "Místo"],
  [563269, "Okounov"],
  [563277, "Otvice"],
  [563285, "Perštejn"],
  [563293, "Pětipsy"],
  [563315, "Kryštofovy Hamry"],
  [563323, "Radonice"],
  [563331, "Rokle"],
  [563340, "Spořice"],
  [563358, "Strupčice"],
  [563382, "Údlice"],
  [563404, "Vejprty"],
  [563412, "Veliká Ves"],
  [563439, "Vilémov"],
  [563463, "Vrskmaň"],
  [563471, "Všehrdy"],
  [563480, "Všestudy"],
  [563498, "Výsluní"],
  [563501, "Vysoká Pec"],
]);

function normalizeDisplayText(value) {
  const raw = String(value ?? "").trim();
  return raw && raw !== "-" ? raw : "";
}

function normalizeAreaCsuCode(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function getAreaCsuCodeFromOkrsek(okrsek) {
  const raw = String(okrsek ?? "").trim();
  if (!raw) return null;
  const firstColon = raw.indexOf(":");
  if (firstColon <= 0) return null;
  return normalizeAreaCsuCode(raw.slice(0, firstColon));
}

function getPrecinctAreaCsuCode(okrsek, areaCsuCode) {
  return normalizeAreaCsuCode(areaCsuCode) ?? getAreaCsuCodeFromOkrsek(okrsek);
}

function getMunicipalityDisplayName(properties) {
  const districtName = normalizeDisplayText(properties?.naz_mco);
  const municipalityName = normalizeDisplayText(properties?.naz_obec);
  if (districtName && municipalityName && districtName.toLowerCase() !== municipalityName.toLowerCase()) {
    return districtName;
  }
  return districtName || municipalityName || "";
}

function getSenMunicipalityNameFallback(areaCsuCode) {
  const normalizedAreaCode = normalizeAreaCsuCode(areaCsuCode);
  if (normalizedAreaCode == null) return "";
  return SEN_MUNICIPALITY_NAME_BY_AREA_CSU_CODE.get(normalizedAreaCode) || "";
}

export function getPrecinctLabel(precinct, electionId, comparableSenAreaCsuCode) {
  const raw =
    typeof precinct === "object" && precinct !== null
      ? String(precinct.raw ?? "").trim()
      : String(precinct ?? "").trim();
  if (!raw) return "";

  const tail = getOkrsekTail(raw);
  if (!isSenElection(electionId)) return `Okrsek ${tail}`;

  const ownAreaCode = getPrecinctAreaCsuCode(
    raw,
    typeof precinct === "object" && precinct !== null ? precinct.areaCsuCode : null
  );
  const comparableAreaCode = normalizeAreaCsuCode(comparableSenAreaCsuCode);
  const municipalityName =
    typeof precinct === "object" && precinct !== null
      ? normalizeDisplayText(precinct.municipalityName) || getSenMunicipalityNameFallback(ownAreaCode)
      : getSenMunicipalityNameFallback(ownAreaCode);

  if (ownAreaCode != null && comparableAreaCode != null) {
    if (ownAreaCode === comparableAreaCode) {
      return `Okrsek ${tail}`;
    }

    if (municipalityName) {
      return `${municipalityName}: ${tail}`;
    }
  }

  if (municipalityName && raw.includes(":")) {
    return `${municipalityName}: ${tail}`;
  }

  return raw;
}

export function getResultDisplayName(row) {
  if (!row || typeof row !== "object") return "";
  const party = String(row.party ?? "").trim();
  if (party) return party;
  return String(row.candidate_name ?? "").trim();
}

function getResultIdentity(row) {
  if (!row || typeof row !== "object") return "";
  const code = row.party_code ?? row.candidate_code ?? getResultDisplayName(row);
  return String(code ?? "").trim();
}

function findResultIdentityByDisplayName(results, displayName) {
  const wanted = String(displayName || "").trim();
  if (!wanted) return "";
  for (const row of Array.isArray(results) ? results : []) {
    for (const result of Array.isArray(row?.results) ? row.results : []) {
      if (getResultDisplayName(result) === wanted) {
        return getResultIdentity(result);
      }
    }
  }
  return "";
}

function isSenElection(electionId) {
  return String(electionId || "").toLowerCase().startsWith("sen");
}

export function getSubjectLabel(electionId) {
  return getPrimaryEntityLabel(parseAppElectionId(electionId).election_key);
}

function collectPrecinctTailsFromGeoJson(districtsGeoJson) {
  const features = Array.isArray(districtsGeoJson?.features) ? districtsGeoJson.features : [];
  const tails = new Set();
  for (const feature of features) {
    const raw =
      feature?.properties?.okrsek ??
      feature?.properties?.district_code ??
      feature?.properties?.okrsek_code;
    const tail = getOkrsekTail(raw);
    if (tail) tails.add(tail);
  }
  return tails;
}

function collectPrecinctTailsFromRows(rows) {
  const tails = new Set();
  for (const row of Array.isArray(rows) ? rows : []) {
    const raw = row?.okrsek ?? row?.district_code ?? row?.okrsek_code;
    const tail = getOkrsekTail(raw);
    if (tail) tails.add(tail);
  }
  return tails;
}

function collectComparablePrecinctTails(districtsGeoJson, results, stats) {
  const fromGeo = collectPrecinctTailsFromGeoJson(districtsGeoJson);
  if (fromGeo.size > 0) return fromGeo;

  const fromResults = collectPrecinctTailsFromRows(results);
  if (fromResults.size > 0) return fromResults;

  return collectPrecinctTailsFromRows(stats);
}

function inferComparableSenAreaCsuCode(
  senDistrictsGeoJson,
  senResults,
  senStats,
  otherDistrictsGeoJson,
  otherResults,
  otherStats
) {
  const comparableTails = collectComparablePrecinctTails(
    otherDistrictsGeoJson,
    otherResults,
    otherStats
  );
  if (comparableTails.size === 0) return null;

  const areaTailSets = new Map();
  const addGeoJsonFeatures = (districtsGeoJson) => {
    const features = Array.isArray(districtsGeoJson?.features) ? districtsGeoJson.features : [];
    for (const feature of features) {
      const raw =
        feature?.properties?.okrsek ??
        feature?.properties?.district_code ??
        feature?.properties?.okrsek_code;
      const areaCode = getPrecinctAreaCsuCode(raw, feature?.properties?.area_csu_code);
      const tail = getOkrsekTail(raw);
      if (areaCode == null || !tail) continue;
      if (!areaTailSets.has(areaCode)) areaTailSets.set(areaCode, new Set());
      areaTailSets.get(areaCode).add(tail);
    }
  };
  const addRows = (rows) => {
    for (const row of Array.isArray(rows) ? rows : []) {
      const areaCode = getPrecinctAreaCsuCode(row?.okrsek, row?.area_csu_code);
      const tail = getOkrsekTail(row?.okrsek ?? row?.district_code ?? row?.okrsek_code);
      if (areaCode == null || !tail) continue;
      if (!areaTailSets.has(areaCode)) areaTailSets.set(areaCode, new Set());
      areaTailSets.get(areaCode).add(tail);
    }
  };

  addGeoJsonFeatures(senDistrictsGeoJson);
  addRows(senResults);
  addRows(senStats);

  const rankedMatches = [];
  for (const [areaCode, tails] of areaTailSets.entries()) {
    let overlapCount = 0;
    for (const tail of comparableTails) {
      if (tails.has(tail)) overlapCount += 1;
    }
    if (overlapCount > 0) rankedMatches.push({ areaCode, overlapCount });
  }

  if (rankedMatches.length === 0) return null;

  rankedMatches.sort((a, b) => {
    if (b.overlapCount !== a.overlapCount) return b.overlapCount - a.overlapCount;
    return a.areaCode - b.areaCode;
  });

  if (rankedMatches.length > 1 && rankedMatches[0].overlapCount === rankedMatches[1].overlapCount) {
    return null;
  }

  return rankedMatches[0].areaCode;
}

function getCrossMapPairingKeyWithSenDisambiguation(
  okrsek,
  ownElectionId,
  otherElectionId,
  ownAreaCsuCode,
  comparableSenAreaCsuCode
) {
  const raw = String(okrsek ?? "").trim();
  if (!raw) return "";

  if (isSenElection(ownElectionId) === isSenElection(otherElectionId)) {
    return raw;
  }

  const tail = getOkrsekTail(raw);
  if (!isSenElection(ownElectionId)) {
    return tail;
  }

  const ownAreaCode = getPrecinctAreaCsuCode(raw, ownAreaCsuCode);
  const comparableAreaCode = normalizeAreaCsuCode(comparableSenAreaCsuCode);
  if (ownAreaCode == null || comparableAreaCode == null) {
    return raw;
  }

  return ownAreaCode === comparableAreaCode ? tail : raw;
}

function toDataLoadErrorMessage(error, fallbackMessage) {
  if (error && Number(error.status) === 403) {
    return "Tato volba není pro váš účet povolena.";
  }
  return fallbackMessage;
}

function escapeHtml(value) {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function GeoJsonStyleUpdater({ geoJsonLayerRef, styleFeature, featureCount }) {
  useEffect(() => {
    const layer = geoJsonLayerRef.current;
    if (!layer || typeof layer.setStyle !== "function" || typeof styleFeature !== "function") return;
    try {
      layer.setStyle(styleFeature);
    } catch (_) {}
  }, [featureCount, geoJsonLayerRef, styleFeature]);
  return null;
}

function GeoJsonBoundsFitter({
  enabled,
  featureCount,
  geoJsonLayerRef,
  padding,
  maxZoom,
  requestKey,
}) {
  const map = useMap();
  const lastAppliedRequestRef = useRef("");

  useEffect(() => {
    if (!enabled || featureCount <= 0) return;
    const resolvedRequestKey = requestKey === undefined ? "initial" : String(requestKey || "");
    if (!resolvedRequestKey) return;
    if (lastAppliedRequestRef.current === resolvedRequestKey) return;
    let cancelled = false;
    const timers = [];
    const schedule = (delay, fn) => {
      const timer = setTimeout(fn, delay);
      timers.push(timer);
    };
    const fitToRenderedBounds = () => {
      if (cancelled) return;
      try {
        const layer = geoJsonLayerRef.current;
        if (!layer || typeof layer.getBounds !== "function") return;
        const bounds = layer.getBounds();
        if (!bounds || typeof bounds.isValid !== "function" || !bounds.isValid()) return;
        const size = typeof map.getSize === "function" ? map.getSize() : null;
        if (size && (Number(size.x) <= 0 || Number(size.y) <= 0)) return;
        map.__volby_programmatic_move_depth = (map.__volby_programmatic_move_depth || 0) + 1;
        if (typeof map.invalidateSize === "function") map.invalidateSize(false);
        map.fitBounds(bounds, { padding, maxZoom });
        lastAppliedRequestRef.current = resolvedRequestKey;
      } catch (_) {
      } finally {
        try {
          map.__volby_programmatic_move_depth = Math.max((map.__volby_programmatic_move_depth || 1) - 1, 0);
        } catch (_) {}
      }
    };

    const runFit = () => {
      fitToRenderedBounds();
      schedule(80, fitToRenderedBounds);
      schedule(240, fitToRenderedBounds);
    };

    if (typeof map.whenReady === "function") {
      try {
        map.whenReady(() => {
          schedule(0, runFit);
        });
      } catch (_) {
        schedule(0, runFit);
      }
    } else {
      schedule(0, runFit);
    }

    return () => {
      cancelled = true;
      timers.forEach((timer) => clearTimeout(timer));
    };
  }, [enabled, featureCount, geoJsonLayerRef, map, maxZoom, padding, requestKey]);

  return null;
}

function getMapPaneName(mapKey, suffix) {
  const key = String(mapKey || "map")
    .replace(/[^a-zA-Z0-9_-]+/g, "-")
    .slice(0, 48);
  return `volby-${key}-${suffix}`;
}

export function MapPane({
  mapKey,
  geoJsonKey,
  districtsGeoJson,
  styleFeature,
  onEachFeature,
  children,
  tileUrl = CARTO_BASE_URL,
  labelsTileUrl = CARTO_LABELS_URL,
  tileAttribution = CARTO_ATTR,
  height = "48vh",
  scrollWheelZoom = true,
  onMapReady,
  onViewChange,
  onGeoJsonReady,
  onMouseLeave,
  onInteractionStart,
  userGestureRef,
  dragging = true,
  doubleClickZoom = true,
  touchZoom = true,
  zoomControl = true,
  showAttribution = true,
  showLabelsLayer = true,
  labelsLayerAboveOverlay = false,
  labelsLayerOpacity = 0.95,
  geoJsonInteractive = true,
  fitGeoJsonBounds = false,
  fitBoundsRequestKey,
  fitBoundsPadding = [20, 20],
  fitBoundsMaxZoom = 14,
  initialView,
  mapStandard,
  baseMapVariant,
  basePaneFilter,
  outsideAreaMask = false,
  outsideAreaMaskGeometry = "precincts",
  outsideAreaMaskOpacity,
  outsideAreaMaskFillColor,
  onResetView,
  resetControlDisabled = false,
}) {
  const localGeoJsonRef = useRef(null);
  const userIntentRef = useRef(false);

  const featureCount = districtsGeoJson?.features?.length || 0;
  const geoJsonRenderKey = geoJsonKey == null ? featureCount : geoJsonKey;
  const resolvedInitialView = normalizeViewport(initialView) || DEFAULT_MAP_VIEW;
  const fitPaddingX = Array.isArray(fitBoundsPadding) ? Number(fitBoundsPadding[0]) || 0 : 20;
  const fitPaddingY = Array.isArray(fitBoundsPadding) ? Number(fitBoundsPadding[1]) || 0 : fitPaddingX;
  const fitPadding = useMemo(() => [fitPaddingX, fitPaddingY], [fitPaddingX, fitPaddingY]);
  const usesStandardV1 = isElectionMapStandardV1(mapStandard);
  const resolvedBaseMapVariant = usesStandardV1 ? ELECTION_MAP_STANDARD_V1.baseMapVariant : baseMapVariant;
  const standardTileUrls = getMapTileUrlsForVariant(resolvedBaseMapVariant);
  const resolvedTileUrl = withCartoBasemapKey(standardTileUrls?.baseTileUrl || tileUrl);
  const resolvedLabelsTileUrl = withCartoBasemapKey(standardTileUrls?.labelsTileUrl || labelsTileUrl);
  const resolvedBasePaneFilter = String(basePaneFilter || "").trim();
  const resolvedLabelsLayerAboveOverlay = usesStandardV1 ? true : labelsLayerAboveOverlay;
  const resolvedOutsideAreaMask = usesStandardV1 || outsideAreaMask;
  const resolvedOutsideAreaMaskGeometry = usesStandardV1 ? "precincts" : outsideAreaMaskGeometry;
  const resolvedOutsideAreaMaskOpacity =
    outsideAreaMaskOpacity ?? (usesStandardV1 ? ELECTION_MAP_STANDARD_V1.outsideMaskOpacity : 0.72);
  const resolvedOutsideAreaMaskFillColor =
    outsideAreaMaskFillColor || ELECTION_MAP_STANDARD_V1.outsideMaskFillColor;
  const maskPositions = useMemo(
    () =>
      resolvedOutsideAreaMask
        ? getOutsideAreaMaskPositions(districtsGeoJson, {
            geometry: resolvedOutsideAreaMaskGeometry,
            fallbackToBounds: false,
          })
        : null,
    [districtsGeoJson, resolvedOutsideAreaMask, resolvedOutsideAreaMaskGeometry]
  );
  const usesLayeredPanes =
    usesStandardV1 ||
    resolvedOutsideAreaMask ||
    resolvedLabelsLayerAboveOverlay ||
    Boolean(resolvedBaseMapVariant) ||
    Boolean(resolvedBasePaneFilter);
  const paneNames = useMemo(() => {
    if (!usesLayeredPanes) return null;
    return {
      base: getMapPaneName(mapKey, "base"),
      mask: getMapPaneName(mapKey, "mask"),
      precinct: getMapPaneName(mapKey, "precinct"),
      labels: getMapPaneName(mapKey, "labels"),
    };
  }, [mapKey, usesLayeredPanes]);
  const paneZIndexes = usesStandardV1 ? ELECTION_MAP_STANDARD_V1.paneZIndexes : {};

  useEffect(() => {
    try {
      onGeoJsonReady && onGeoJsonReady(localGeoJsonRef.current);
    } catch (_) {}
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [featureCount, geoJsonRenderKey]);

  function MapReady() {
    const map = useMap();
    useEffect(() => {
      // react-leaflet versions differ in whenCreated support; this is robust.
      try {
        onMapReady && onMapReady(map);
      } catch (_) {}

      // DOM-level intent: reliably marks user-initiated interactions even when the drag starts on polygons.
      let el = null;
      const onPointerDown = () => {
        try {
          if ((map.__volby_programmatic_move_depth || 0) > 0) return;
          userIntentRef.current = true;
          onInteractionStart && onInteractionStart();
        } catch (_) {}
      };
      const onWheel = () => {
        try {
          if (!scrollWheelZoom) return;
          if ((map.__volby_programmatic_move_depth || 0) > 0) return;
          userIntentRef.current = true;
          onInteractionStart && onInteractionStart();
        } catch (_) {}
      };

      try {
        el = typeof map.getContainer === "function" ? map.getContainer() : null;
        if (el && typeof el.addEventListener === "function") {
          el.addEventListener("pointerdown", onPointerDown, { passive: true, capture: true });
          el.addEventListener("wheel", onWheel, { passive: true, capture: true });
        }
      } catch (_) {}

      return () => {
        try {
          el && el.removeEventListener && el.removeEventListener("pointerdown", onPointerDown, true);
        } catch (_) {}
        try {
          el && el.removeEventListener && el.removeEventListener("wheel", onWheel, true);
        } catch (_) {}
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [map, onInteractionStart, onMapReady, scrollWheelZoom]);
    return null;
  }

  function ViewSyncEvents() {
    // Sync rule:
    // - programmatic moves (fitBounds/setView from code) do NOT propagate
    // - user-initiated moves (drag/zoom via real input) DO propagate
    const userZoomRef = useRef(false);
    const gestureRef = userGestureRef;

    useMapEvents({
      moveend: (e) => {
        try {
          const m = e?.target;
          const depth = m?.__volby_programmatic_move_depth || 0;
          const shouldSync =
            !!m &&
            depth === 0 &&
            (userIntentRef.current === true || gestureRef?.current === true);

          userIntentRef.current = false;
          if (gestureRef?.current != null) gestureRef.current = false;
          if (!shouldSync) return;
          onViewChange && onViewChange(m.getCenter(), m.getZoom());
        } catch (_) {}
      },
      zoomstart: (e) => {
        try {
          const m = e?.target;
          if (!m) return;
          if (m.__volby_programmatic_move_depth > 0) return;
          userZoomRef.current = true;
          onInteractionStart && onInteractionStart();
        } catch (_) {}
      },
      zoomend: (e) => {
        const m = e?.target;
        const depth = (m?.__volby_programmatic_move_depth || 0);
        const shouldSync =
          !!m &&
          depth === 0 &&
          (userZoomRef.current === true || userIntentRef.current === true || gestureRef?.current === true);

        userZoomRef.current = false;
        userIntentRef.current = false;
        if (gestureRef?.current != null) gestureRef.current = false;
        if (!shouldSync) return;

        try {
          onViewChange && onViewChange(m.getCenter(), m.getZoom());
        } catch (_) {}
      },
    });
    return null;
  }

  return (
    <div
      onMouseLeave={onMouseLeave}
      style={{ border: "1px solid #e5e7eb", borderRadius: 10, overflow: "hidden" }}
    >
      <MapContainer
        style={{ height, width: "100%" }}
        center={resolvedInitialView.center}
        zoom={resolvedInitialView.zoom}
        scrollWheelZoom={scrollWheelZoom}
        dragging={dragging}
        doubleClickZoom={doubleClickZoom}
        touchZoom={touchZoom}
        zoomControl={zoomControl}
        keyboard={false}
        boxZoom={false}
        attributionControl={false}
      >
        {showAttribution ? <AttributionControl position="bottomright" prefix={ATTRIBUTION_PREFIX} /> : null}
        {typeof onResetView === "function" ? (
          <div className="leaflet-top leaflet-right election-map-reset-control">
            <div className="leaflet-control leaflet-bar">
              <button
                type="button"
                className="election-map-reset-button"
                title="Reset"
                aria-label="Reset"
                disabled={resetControlDisabled}
                onPointerDown={(event) => {
                  event.stopPropagation();
                }}
                onMouseDown={(event) => {
                  event.stopPropagation();
                }}
                onDoubleClick={(event) => {
                  event.stopPropagation();
                }}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  if (!resetControlDisabled) onResetView();
                }}
              >
                <span aria-hidden="true">↻</span>
              </button>
            </div>
          </div>
        ) : null}
        {usesLayeredPanes && paneNames ? (
          <Pane
            name={paneNames.base}
            style={{
              zIndex: paneZIndexes.base ?? 200,
              filter: resolvedBasePaneFilter || undefined,
            }}
          >
            <TileLayer url={resolvedTileUrl} attribution={tileAttribution} />
          </Pane>
        ) : (
          <TileLayer url={resolvedTileUrl} attribution={tileAttribution} />
        )}
        {showLabelsLayer && !resolvedLabelsLayerAboveOverlay ? (
          usesLayeredPanes && paneNames ? (
            <Pane name={paneNames.labels} style={{ zIndex: paneZIndexes.labels ?? 520, pointerEvents: "none" }}>
              <TileLayer url={resolvedLabelsTileUrl} opacity={labelsLayerOpacity} />
            </Pane>
          ) : (
            <TileLayer url={resolvedLabelsTileUrl} opacity={labelsLayerOpacity} />
          )
        ) : null}
        <MapReady />
        <ViewSyncEvents />
        {usesLayeredPanes && paneNames && maskPositions ? (
          <Pane name={paneNames.mask} style={{ zIndex: paneZIndexes.mask ?? 300, pointerEvents: "none" }}>
            <Polygon
              positions={maskPositions}
              interactive={false}
              pathOptions={{
                color: "transparent",
                weight: 0,
                opacity: 0,
                fillColor: resolvedOutsideAreaMaskFillColor,
                fillOpacity: resolvedOutsideAreaMaskOpacity,
                fillRule: "evenodd",
              }}
            />
          </Pane>
        ) : null}
        {usesLayeredPanes && paneNames ? (
          <Pane name={paneNames.precinct} style={{ zIndex: paneZIndexes.precinct ?? 410 }}>
            <GeoJSON
              key={`${mapKey}-geo-${geoJsonRenderKey}`}
              ref={localGeoJsonRef}
              data={districtsGeoJson}
              style={styleFeature}
              onEachFeature={onEachFeature}
              interactive={geoJsonInteractive}
            />
          </Pane>
        ) : (
          <GeoJSON
            key={`${mapKey}-geo-${geoJsonRenderKey}`}
            ref={localGeoJsonRef}
            data={districtsGeoJson}
            style={styleFeature}
            onEachFeature={onEachFeature}
            interactive={geoJsonInteractive}
          />
        )}
        {showLabelsLayer && resolvedLabelsLayerAboveOverlay ? (
          usesLayeredPanes && paneNames ? (
            <Pane name={paneNames.labels} style={{ zIndex: paneZIndexes.labels ?? 520, pointerEvents: "none" }}>
              <TileLayer url={resolvedLabelsTileUrl} opacity={labelsLayerOpacity} />
            </Pane>
          ) : (
            <TileLayer url={resolvedLabelsTileUrl} opacity={labelsLayerOpacity} />
          )
        ) : null}
        <GeoJsonStyleUpdater
          geoJsonLayerRef={localGeoJsonRef}
          styleFeature={styleFeature}
          featureCount={featureCount}
        />
        <GeoJsonBoundsFitter
          enabled={fitGeoJsonBounds}
          featureCount={featureCount}
          geoJsonLayerRef={localGeoJsonRef}
          padding={fitPadding}
          maxZoom={fitBoundsMaxZoom}
          requestKey={fitBoundsRequestKey}
        />
        {children}
      </MapContainer>
      <style>{`
        .election-map-reset-control {
          z-index: 1000;
        }
        .election-map-reset-button {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 30px;
          height: 30px;
          border: 0;
          border-radius: 0;
          background: #fff;
          color: #111827;
          font: 700 18px/1 Arial, Helvetica, sans-serif;
          cursor: pointer;
          padding: 0;
        }
        .election-map-reset-button:hover,
        .election-map-reset-button:focus-visible {
          background: #f4f4f4;
        }
        .election-map-reset-button:disabled {
          color: #9ca3af;
          cursor: default;
          background: #f8fafc;
        }
      `}</style>
    </div>
  );
}

export function useElectionData(electionId, areaId, apiBase = "/api") {
  const [districtsGeoJson, setDistrictsGeoJson] = useState({
    type: "FeatureCollection",
    features: [],
  });
  const [results, setResults] = useState([]);
  const [stats, setStats] = useState([]);

  const [districtsError, setDistrictsError] = useState("");
  const [resultsError, setResultsError] = useState("");
  const [statsError, setStatsError] = useState("");

  const [isLoadingDistricts, setIsLoadingDistricts] = useState(false);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [isLoadingStats, setIsLoadingStats] = useState(false);

  useEffect(() => {
    if (!electionId || !areaId) {
      setDistrictsGeoJson({ type: "FeatureCollection", features: [] });
      setResults([]);
      setStats([]);
      setDistrictsError("");
      setResultsError("");
      setStatsError("");
      setIsLoadingDistricts(false);
      setIsLoadingResults(false);
      setIsLoadingStats(false);
      return;
    }

    const ac = new AbortController();

    // Immediate reset on election change so old data never “sticks”
    setDistrictsGeoJson({ type: "FeatureCollection", features: [] });
    setResults([]);
    setStats([]);
    setDistrictsError("");
    setResultsError("");
    setStatsError("");

    (async () => {
      // Districts
      try {
        setIsLoadingDistricts(true);
        setDistrictsError("");
        const url = buildAreaDataUrl("districts/geojson", areaId, electionId, apiBase);
        console.log("[Map] fetching districts geojson:", url);
        const r = await fetch(url, buildApiFetchOptions(apiBase, { signal: ac.signal }));
        if (!r.ok) throw createHttpError(r.status, "districts geojson");
        const data = await r.json();
        if (ac.signal.aborted) return;

        const ok = data && data.type === "FeatureCollection" && Array.isArray(data.features);
        const fc = ok ? data : { type: "FeatureCollection", features: [] };

        setDistrictsGeoJson(fc);
        console.log("[Map] districts geojson features:", fc.features.length);
      } catch (e) {
        if (e?.name !== "AbortError") {
          setDistrictsGeoJson({ type: "FeatureCollection", features: [] });
          setDistrictsError(toDataLoadErrorMessage(
            e,
            "Nepodařilo se načíst okrsky (backend neběží nebo vrací chybu). Zkontroluj, že běží backend a že je dostupný přes /api."
          ));
          console.error("[Map] failed to load districts", e);
        }
      } finally {
        setIsLoadingDistricts(false);
      }

      // Results
      try {
        setIsLoadingResults(true);
        setResultsError("");
        const url = buildAreaDataUrl("results", areaId, electionId, apiBase);
        const r = await fetch(url, buildApiFetchOptions(apiBase, { signal: ac.signal }));
        if (!r.ok) throw createHttpError(r.status, "results");
        const data = await r.json();
        if (ac.signal.aborted) return;
        setResults(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e?.name !== "AbortError") {
          setResults([]);
          setResultsError(toDataLoadErrorMessage(
            e,
            "Nepodařilo se načíst výsledky (backend neběží nebo vrací chybu). Zkontroluj, že běží backend a že je dostupný přes /api."
          ));
          console.error("[Map] failed to load results", e);
        }
      } finally {
        setIsLoadingResults(false);
      }

      // Stats
      try {
        setIsLoadingStats(true);
        setStatsError("");
        const url = buildAreaDataUrl("stats", areaId, electionId, apiBase);
        const r = await fetch(url, buildApiFetchOptions(apiBase, { signal: ac.signal }));
        if (!r.ok) throw createHttpError(r.status, "stats");
        const data = await r.json();
        if (ac.signal.aborted) return;
        setStats(Array.isArray(data) ? data : []);
      } catch (e) {
        if (e?.name !== "AbortError") {
          setStats([]);
          setStatsError(toDataLoadErrorMessage(
            e,
            "Nepodařilo se načíst statistiky (voliči/účast). Zkontroluj, že backend běží a že je dostupný přes /api."
          ));
          console.error("[Map] failed to load stats", e);
        }
      } finally {
        setIsLoadingStats(false);
      }
    })();

    return () => ac.abort();
  }, [electionId, areaId, apiBase]);

  return {
    districtsGeoJson,
    results,
    stats,
    districtsError,
    resultsError,
    statsError,
    isLoadingDistricts,
    isLoadingResults,
    isLoadingStats,
  };
}


export function getPartiesFromResults(results) {
  for (const row of results) {
    if (row && Array.isArray(row.results) && row.results.length > 0) {
      return row.results.map((x) => getResultDisplayName(x)).filter(Boolean);
    }
  }
  return [];
}

function buildLoadingLabel({ isLoadingDistricts, isLoadingResults, isLoadingStats }) {
  const parts = [];
  if (isLoadingDistricts) parts.push("okrsky");
  if (isLoadingResults) parts.push("výsledky");
  if (isLoadingStats) parts.push("statistiky");
  return parts.length > 0 ? `Načítám: ${parts.join(" + ")}` : "";
}

function MapStatusCard({
  districtsError,
  resultsError,
  statsError,
  isLoadingDistricts,
  isLoadingResults,
  isLoadingStats,
}) {
  const uniqueErrors = useMemo(
    () => Array.from(new Set([districtsError, resultsError, statsError].filter(Boolean))),
    [districtsError, resultsError, statsError]
  );
  const loadingLabel = buildLoadingLabel({ isLoadingDistricts, isLoadingResults, isLoadingStats });

  if (!loadingLabel && uniqueErrors.length === 0) return null;

  return (
    <div className="compare-map-status">
      {loadingLabel ? <div className="compare-status-pill">{loadingLabel}</div> : null}
      {uniqueErrors.length > 0 ? (
        <div className="compare-error-list">
          {uniqueErrors.map((message, idx) => (
            <div key={`${message}-${idx}`}>{message}</div>
          ))}
        </div>
      ) : null}
    </div>
  );
}

function MapControlCard({
  title,
  elections,
  electionId,
  setElectionId,
  parties,
  selectedParty,
  setSelectedParty,
  baseColor,
  setBaseColor,
  legendItems,
}) {
  const subjectLabel = getSubjectLabel(electionId);
  const paletteSteps = getPaletteSteps(baseColor);

  return (
    <section className="compare-card compare-controls-card">
      <div className="compare-controls-body">
        <div className="compare-controls-column">
          <label className="compare-control-stack">
            <span className="compare-control-label">Volby</span>
            <select
              className="compare-select"
              value={electionId}
              onChange={(e) => setElectionId(e.target.value)}
            >
              {elections.map((el) => (
                <option key={el.id} value={String(el.id)}>
                  {el.display_name || el.name || el.id}
                </option>
              ))}
            </select>
          </label>

          <label className="compare-control-stack">
            <span className="compare-control-label">{subjectLabel}</span>
            <select
              className="compare-select"
              value={selectedParty}
              onChange={(e) => setSelectedParty(e.target.value)}
            >
              {parties.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </label>

          <label className="compare-control-stack">
            <span className="compare-control-label">Barva škály</span>
            <div className="compare-color-row">
              <span className="compare-color-preview" aria-hidden="true">
                {paletteSteps.map((color) => (
                  <span
                    key={color}
                    className="compare-color-preview-step"
                    style={{ background: color }}
                  />
                ))}
              </span>
              <select
                className="compare-select"
                value={baseColor}
                onChange={(e) => setBaseColor(e.target.value)}
              >
                {COLOR_PRESETS.map((p) => (
                  <option key={p.hex} value={p.hex}>
                    {p.name}
                  </option>
                ))}
              </select>
            </div>
          </label>
        </div>

        <div className="compare-legend-block">
          <div className="compare-legend-heading">
            <span className="compare-control-label">{`Legenda - ${title}`}</span>
          </div>
          <div className="compare-legend-list">
            {legendItems.map((it) => (
              <div key={it.label} className="compare-legend-row">
                <span
                  className="compare-legend-swatch"
                  style={{ background: it.color }}
                />
                <span>{it.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function ClickedPanel({
  title,
  clickedOkrsek,
  clickedDisplayPrecinct,
  clickedResults,
  clickedStats,
  comparableSenAreaCsuCode,
  selectedParty,
  baseColor,
}) {
  const rows = (Array.isArray(clickedResults) ? [...clickedResults] : []).sort((a, b) => {
    const ap = a?.percentage ?? -1;
    const bp = b?.percentage ?? -1;
    if (bp !== ap) return bp - ap;

    const av = a?.votes ?? 0;
    const bv = b?.votes ?? 0;
    if (bv !== av) return bv - av;

    return getResultDisplayName(a).localeCompare(getResultDisplayName(b));
  });
  const electionId = clickedStats?.electionId || null;
  const precinctLabel = getPrecinctLabel(
    clickedDisplayPrecinct || clickedOkrsek,
    electionId,
    comparableSenAreaCsuCode
  );
  const subjectLabel = getSubjectLabel(electionId);
  const votersText = clickedStats?.voters != null ? formatInt(clickedStats.voters) : "–";
  const totalVotesText = clickedStats?.totalVotes != null ? formatInt(clickedStats.totalVotes) : "–";
  const turnoutText = clickedStats?.turnoutPct != null ? formatPct(clickedStats.turnoutPct) : "–";
  const paletteSteps = getPaletteSteps(baseColor);
  const selectedRow = rows.find((row) => getResultDisplayName(row) === selectedParty) || null;
  const selectedPct = selectedRow?.percentage != null ? formatPct(selectedRow.percentage) : "–";
  const selectedVotes = selectedRow?.votes != null ? formatInt(selectedRow.votes) : "–";

  return (
    <section className="compare-card compare-pinned-card">
      {!clickedOkrsek ? (
        <div className="compare-pinned-empty">Klikni na okrsek v mapě a detail se připne sem.</div>
      ) : (
        <>
          <div className="compare-pinned-summary">
            <div className="compare-pinned-topline">
              <div className="compare-pinned-title">{precinctLabel}</div>
            </div>
            <div
              className="compare-selected-party-card"
              style={{
                borderColor: mixHex(paletteSteps[2], "#ffffff", 0.45),
                background: `linear-gradient(180deg, ${mixHex(paletteSteps[0], "#ffffff", 0.18)} 0%, ${mixHex(paletteSteps[1], "#ffffff", 0.32)} 100%)`,
              }}
            >
              <div className="compare-selected-party-label">{selectedParty || subjectLabel}</div>
              <div className="compare-selected-party-stats-line">
                <div className="compare-selected-party-value" style={{ color: paletteSteps[3] }}>
                  {selectedPct}
                </div>
                <div className="compare-selected-party-meta">
                  {selectedVotes} hlasů
                </div>
              </div>
            </div>
            <div className="compare-stat-grid">
              <div className="compare-stat-box">
                <span className="compare-stat-label">Voliči</span>
                <strong>{votersText}</strong>
              </div>
              <div className="compare-stat-box">
                <span className="compare-stat-label">Hlasující</span>
                <strong>{totalVotesText}</strong>
              </div>
              <div className="compare-stat-box">
                <span className="compare-stat-label">Účast</span>
                <strong>{turnoutText}</strong>
              </div>
            </div>
          </div>

          <div className="compare-results-table">
            <div className="compare-results-head">
              <div className="compare-results-header">{subjectLabel}</div>
              <div className="compare-results-header compare-results-header-right">Hlasy</div>
              <div className="compare-results-header compare-results-header-right">%</div>
            </div>

            {rows.map((r) => {
              const isSelected = getResultDisplayName(r) === selectedParty;
              return (
                <div
                  key={getResultIdentity(r)}
                  className={`compare-results-row ${isSelected ? "is-selected" : ""}`}
                  style={
                    isSelected
                      ? {
                          borderLeftColor: paletteSteps[3],
                          background: mixHex(paletteSteps[0], "#ffffff", 0.22),
                        }
                      : undefined
                  }
                >
                  <span className="compare-results-name">
                    {getResultDisplayName(r)}
                  </span>
                  <span className="compare-results-value">
                    {r.votes != null ? formatInt(r.votes) : "–"}
                  </span>
                  <span className="compare-results-value">
                    {r.percentage != null ? formatPct(r.percentage) : "–"}
                  </span>
                </div>
              );
            })}
          </div>
        </>
      )}
    </section>
  );
}
export function formatInt(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "–";
  return Math.round(x).toLocaleString("cs-CZ");
}

export function formatPct(n) {
  const x = Number(n);
  if (!Number.isFinite(x)) return "–";
  return `${x.toLocaleString("cs-CZ", { maximumFractionDigits: 1, minimumFractionDigits: 1 })}%`;
}

function isKvElection(electionId) {
  return String(electionId || "").toLowerCase().startsWith("kv");
}

export function getStatsForOkrsek(statsByOkrsek, okrsek, electionId) {
  const s = statsByOkrsek.get(String(okrsek));
  if (!s) return null;
  const kvElection = isKvElection(electionId);
  const voters = s.VOL_SEZNAM ?? s.vol_seznam ?? s.voters ?? null;
  const totalVotes =
    kvElection
      ? (s.ODEVZ_OBALEK ?? s.odevz_obalek ?? s.ODEVZ_OBAL ?? s.odevz_obal ?? s.total_votes ?? null)
      : (s.PL_HL_CELK ?? s.pl_hl_celk ?? s.ODEVZ_OBALEK ?? s.ODEVZ_OBAL ?? s.HLASY_CELK ?? s.total_votes ?? null);

  let turnoutPct = s.UCAST_PCT ?? s.ucast_pct ?? null;
  if (turnoutPct == null && voters != null && totalVotes != null) {
    const v = Number(voters);
    const t = Number(totalVotes);
    if (Number.isFinite(v) && v > 0 && Number.isFinite(t)) {
      turnoutPct = (t / v) * 100;
    }
  }

  return {
    electionId,
    voters,
    totalVotes,
    turnoutPct,
  };
}

export default function ElectionMap({ areaId, apiBase = "/api", enableReports = true }) {
  const mapARef = useRef(null);
  const mapBRef = useRef(null);
  const userGestureARef = useRef(false);
  const userGestureBRef = useRef(false);
  const [mapAReady, setMapAReady] = useState(false);
  const [mapBReady, setMapBReady] = useState(false);
  const [isWideLayout, setIsWideLayout] = useState(() => {
    if (typeof window === "undefined") return true;
    return window.innerWidth >= WIDE_LAYOUT_BREAKPOINT;
  });

  // elections list (shared)
  const [elections, setElections] = useState([]);

  const initialSettings = useMemo(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : null;
    } catch (_) {
      return null;
    }
  }, []);

  // Map A controls
  const [electionIdA, setElectionIdA] = useState("");
  const [selectedPartyA, setSelectedPartyA] = useState("");
  const [baseColorA, setBaseColorA] = useState(() =>
    getStoredPresetColor(initialSettings?.A?.baseColorA, BRAND_COLORS.primaryBrightBlue)
  );

  // Map B controls
  const [electionIdB, setElectionIdB] = useState("");
  const [selectedPartyB, setSelectedPartyB] = useState("");
  const [baseColorB, setBaseColorB] = useState(() =>
    getStoredPresetColor(initialSettings?.B?.baseColorB, BRAND_COLORS.accentRed)
  );

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      const next = {
        A: {
          baseColorA,
        },
        B: {
          baseColorB,
        },
      };
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch (_) {
      // ignore
    }
  }, [baseColorA, baseColorB]);

  // Shared clicked okrsek state for both maps
  const [clickedOkrsek, setClickedOkrsek] = useState(null);

  // Shared hover okrsek state for both maps
  const [hoveredOkrsek, setHoveredOkrsek] = useState(null);

  // Shared deterministic initial bounds (same districts for both maps).
  // Stored as a simple LatLngBoundsLike array so we can reuse it across Leaflet instances.
  const sharedBoundsRef = useRef(null);
  const sharedBoundsSigRef = useRef("");
  const [sharedBoundsSig, setSharedBoundsSig] = useState("");
  const initialViewport = useMemo(() => getInitialMapView("app", areaId), [areaId]);
  const hasStoredInitialViewportRef = useRef(Boolean(readStoredViewport("app", areaId)));
  const [fitRequestKey, setFitRequestKey] = useState(() =>
    hasStoredInitialViewportRef.current ? "" : makeAutoFitRequestKey("app", areaId, "initial")
  );
  const prevAreaIdForViewportRef = useRef(areaId);
  const lastFitAppliedRef = useRef({ A: "", B: "" });

  const withProgrammaticMove = useCallback((map, fn) => {
    if (!map || typeof fn !== "function") return;
    map.__volby_programmatic_move_depth = (map.__volby_programmatic_move_depth || 0) + 1;

    let done = false;
    let sawMoveEnd = false;
    let sawZoomStart = false;
    let sawZoomEnd = false;
    let fallbackTimer = null;

    function decDepth() {
      try {
        map.__volby_programmatic_move_depth = Math.max(0, (map.__volby_programmatic_move_depth || 1) - 1);
      } catch (_) {}
    }

    function cleanup() {
      try { map.off("moveend", onMoveEnd); } catch (_) {}
      try { map.off("zoomstart", onZoomStart); } catch (_) {}
      try { map.off("zoomend", onZoomEnd); } catch (_) {}
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
    }

    function finish() {
      if (done) return;
      done = true;
      cleanup();
      // Decrement after Leaflet dispatch completes for the settling event(s).
      setTimeout(decDepth, 0);
    }

    function maybeFinish() {
      if (!sawMoveEnd) return;
      if (sawZoomStart && !sawZoomEnd) return;
      finish();
    }

    function onMoveEnd() {
      sawMoveEnd = true;
      maybeFinish();
    }

    function onZoomStart() {
      sawZoomStart = true;
    }

    function onZoomEnd() {
      sawZoomEnd = true;
      maybeFinish();
    }

    // Attach listeners before fn() in case Leaflet fires synchronously.
    try { map.once("moveend", onMoveEnd); } catch (_) {}
    try { map.once("zoomstart", onZoomStart); } catch (_) {}
    try { map.once("zoomend", onZoomEnd); } catch (_) {}

    // Fallback so we never get stuck in "programmatic" mode.
    fallbackTimer = setTimeout(() => {
      finish();
    }, 250);

    try {
      fn();
    } catch (e) {
      // Ensure we don't keep the map in "programmatic" state on errors.
      finish();
      throw e;
    }
  }, []);

  useEffect(() => {
    if (prevAreaIdForViewportRef.current === areaId) return;
    prevAreaIdForViewportRef.current = areaId;
    sharedBoundsRef.current = null;
    sharedBoundsSigRef.current = "";
    setSharedBoundsSig("");
    lastFitAppliedRef.current = { A: "", B: "" };
    clearStoredViewport("app", areaId);
    setFitRequestKey(makeAutoFitRequestKey("app", areaId, "area"));
  }, [areaId]);

  // Index okrsek -> Leaflet layer for each map (lets us sync hover without scanning all layers)
  const layerIndexARef = useRef(new Map());
  const layerIndexBRef = useRef(new Map());
  // One hover tooltip instance per map (prevents orphan/stacked tooltips).
  const hoverTooltipARef = useRef(null);
  const hoverTooltipBRef = useRef(null);
  const lastHoverLatLngRef = useRef(null);
  const prevHoveredRef = useRef(null);
  const hoverClearTimerRef = useRef(null);
  const prevElectionIdARef = useRef(electionIdA);
  const prevElectionIdBRef = useRef(electionIdB);
  const prevPairingSignatureARef = useRef("");
  const prevPairingSignatureBRef = useRef("");

  const ensureHoverTooltip = useCallback((map, ref) => {
    if (!map) return null;
    if (ref.current) return ref.current;
    try {
      ref.current = L.tooltip({
        sticky: true,
        direction: "top",
        offset: [12, -24],
        opacity: 0.95,
        className: "hover-tooltip",
      });
    } catch (_) {
      ref.current = null;
    }
    return ref.current;
  }, []);

  const showHoverTooltip = useCallback((map, ref, latlng, html) => {
    if (!map || !latlng) return;
    const tt = ensureHoverTooltip(map, ref);
    if (!tt) return;
    try {
      tt.setLatLng(latlng);
      tt.setContent(html);
      tt.addTo(map);
    } catch (_) {}
  }, [ensureHoverTooltip]);

  const hideHoverTooltip = useCallback((map, ref) => {
    const tt = ref.current;
    if (!tt) return;
    try {
      if (map && typeof map.removeLayer === "function") map.removeLayer(tt);
      else if (typeof tt.remove === "function") tt.remove();
    } catch (_) {}
    ref.current = null;
  }, []);

  // Load elections once
  useEffect(() => {
    if (!areaId) {
      setElections([]);
      setElectionIdA("");
      setElectionIdB("");
      return () => {};
    }

    setElections([]);
    setElectionIdA("");
    setElectionIdB("");

    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(buildAreaElectionsUrl(areaId, apiBase), buildApiFetchOptions(apiBase));
        if (!r.ok) throw createHttpError(r.status, "enabled elections");
        const data = await r.json();
        if (cancelled) return;
        const list = sortElectionsNewestFirst(Array.isArray(data) ? data : []);
        setElections(list);

        if (list.length === 0) {
          setElectionIdA("");
          setElectionIdB("");
          return;
        }

        const firstId = String(list[0].id);
        const storedSelection = readElectionSelectionContext(areaId);

        setElectionIdA((prev) =>
          pickValidElectionId(
            list,
            [prev, storedSelection.appElectionIdA, storedSelection.analyticsElectionId, storedSelection.lastElectionId],
            firstId
          )
        );
        setElectionIdB((prev) =>
          pickValidElectionId(
            list,
            [prev, storedSelection.appElectionIdB, storedSelection.analyticsElectionId, storedSelection.lastElectionId],
            firstId
          )
        );
      } catch (e) {
        console.error("[Map] failed to load elections", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [areaId, apiBase]);

  useEffect(() => {
    if (!areaId || !electionIdA) return;
    const storedSelection = readElectionSelectionContext(areaId);
    writeElectionSelectionContext(areaId, {
      appElectionIdA: electionIdA,
      appPartyAByElection: setStoredChoiceForElection(
        storedSelection,
        "appPartyAByElection",
        electionIdA,
        selectedPartyA
      ),
      lastElectionId: electionIdA,
    });
  }, [areaId, electionIdA, selectedPartyA]);

  useEffect(() => {
    if (!areaId || !electionIdB) return;
    const storedSelection = readElectionSelectionContext(areaId);
    writeElectionSelectionContext(areaId, {
      appElectionIdB: electionIdB,
      appPartyBByElection: setStoredChoiceForElection(
        storedSelection,
        "appPartyBByElection",
        electionIdB,
        selectedPartyB
      ),
      lastElectionId: electionIdB,
    });
  }, [areaId, electionIdB, selectedPartyB]);

  // Data per map
  const dataA = useElectionData(electionIdA, areaId, apiBase);
  const dataB = useElectionData(electionIdB, areaId, apiBase);

  const comparableSenAreaCsuCodeA = useMemo(() => {
    if (!isSenElection(electionIdA) || isSenElection(electionIdB)) return null;
    return inferComparableSenAreaCsuCode(
      dataA.districtsGeoJson,
      dataA.results,
      dataA.stats,
      dataB.districtsGeoJson,
      dataB.results,
      dataB.stats
    );
  }, [dataA.districtsGeoJson, dataA.results, dataA.stats, dataB.districtsGeoJson, dataB.results, dataB.stats, electionIdA, electionIdB]);

  const comparableSenAreaCsuCodeB = useMemo(() => {
    if (!isSenElection(electionIdB) || isSenElection(electionIdA)) return null;
    return inferComparableSenAreaCsuCode(
      dataB.districtsGeoJson,
      dataB.results,
      dataB.stats,
      dataA.districtsGeoJson,
      dataA.results,
      dataA.stats
    );
  }, [dataA.districtsGeoJson, dataA.results, dataA.stats, dataB.districtsGeoJson, dataB.results, dataB.stats, electionIdA, electionIdB]);

  const boundaryComparisonBlocked = hasPrecinctBoundaryBreak(electionIdA, electionIdB);

  const pairingSignatureA = useMemo(
    () => `${normalizeAreaCsuCode(comparableSenAreaCsuCodeA) ?? "none"}:${boundaryComparisonBlocked}`,
    [comparableSenAreaCsuCodeA, boundaryComparisonBlocked]
  );
  const pairingSignatureB = useMemo(
    () => `${normalizeAreaCsuCode(comparableSenAreaCsuCodeB) ?? "none"}:${boundaryComparisonBlocked}`,
    [comparableSenAreaCsuCodeB, boundaryComparisonBlocked]
  );

  const getPairKeyA = useCallback(
    (okrsek, areaCsuCode) =>
      scopePrecinctPairKey(getCrossMapPairingKeyWithSenDisambiguation(
        okrsek,
        electionIdA,
        electionIdB,
        areaCsuCode,
        comparableSenAreaCsuCodeA
      ), "A", boundaryComparisonBlocked),
    [comparableSenAreaCsuCodeA, electionIdA, electionIdB, boundaryComparisonBlocked]
  );

  const getPairKeyB = useCallback(
    (okrsek, areaCsuCode) =>
      scopePrecinctPairKey(getCrossMapPairingKeyWithSenDisambiguation(
        okrsek,
        electionIdB,
        electionIdA,
        areaCsuCode,
        comparableSenAreaCsuCodeB
      ), "B", boundaryComparisonBlocked),
    [comparableSenAreaCsuCodeB, electionIdA, electionIdB, boundaryComparisonBlocked]
  );

  const displayPrecinctByPairKeyA = useMemo(() => {
    const m = new Map();
    const features = Array.isArray(dataA.districtsGeoJson?.features) ? dataA.districtsGeoJson.features : [];
    for (const feature of features) {
      const raw = feature?.properties?.okrsek;
      const pairKey = getPairKeyA(raw, feature?.properties?.area_csu_code);
      if (pairKey && !m.has(String(pairKey))) {
        m.set(String(pairKey), {
          raw: String(raw),
          areaCsuCode: feature?.properties?.area_csu_code,
          municipalityName: getMunicipalityDisplayName(feature?.properties),
        });
      }
    }
    return m;
  }, [dataA.districtsGeoJson, getPairKeyA]);

  const displayPrecinctByPairKeyB = useMemo(() => {
    const m = new Map();
    const features = Array.isArray(dataB.districtsGeoJson?.features) ? dataB.districtsGeoJson.features : [];
    for (const feature of features) {
      const raw = feature?.properties?.okrsek;
      const pairKey = getPairKeyB(raw, feature?.properties?.area_csu_code);
      if (pairKey && !m.has(String(pairKey))) {
        m.set(String(pairKey), {
          raw: String(raw),
          areaCsuCode: feature?.properties?.area_csu_code,
          municipalityName: getMunicipalityDisplayName(feature?.properties),
        });
      }
    }
    return m;
  }, [dataB.districtsGeoJson, getPairKeyB]);

  // resultsByOkrsek per map
  const resultsByPairKeyA = useMemo(() => {
    const m = new Map();
    for (const row of dataA.results) {
      if (!row || row.okrsek == null) continue;
      const pairKey = getPairKeyA(row.okrsek, row.area_csu_code);
      if (pairKey) m.set(pairKey, row.results || []);
    }
    return m;
  }, [dataA.results, getPairKeyA]);

  const resultsByPairKeyB = useMemo(() => {
    const m = new Map();
    for (const row of dataB.results) {
      if (!row || row.okrsek == null) continue;
      const pairKey = getPairKeyB(row.okrsek, row.area_csu_code);
      if (pairKey) m.set(pairKey, row.results || []);
    }
    return m;
  }, [dataB.results, getPairKeyB]);

  const statsByPairKeyA = useMemo(() => {
    const m = new Map();
    for (const row of dataA.stats || []) {
      if (!row || row.okrsek == null) continue;
      const pairKey = getPairKeyA(row.okrsek, row.area_csu_code);
      if (pairKey) m.set(pairKey, row);
    }
    return m;
  }, [dataA.stats, getPairKeyA]);

  const statsByPairKeyB = useMemo(() => {
    const m = new Map();
    for (const row of dataB.stats || []) {
      if (!row || row.okrsek == null) continue;
      const pairKey = getPairKeyB(row.okrsek, row.area_csu_code);
      if (pairKey) m.set(pairKey, row);
    }
    return m;
  }, [dataB.stats, getPairKeyB]);

  // parties per map
  const partiesA = useMemo(() => getPartiesFromResults(dataA.results), [dataA.results]);
  const partiesB = useMemo(() => getPartiesFromResults(dataB.results), [dataB.results]);

  // default party selection
  useEffect(() => {
    // When election changes, parties list changes. Ensure the selected party is always valid.
    if (!partiesA || partiesA.length === 0) {
      if (selectedPartyA) setSelectedPartyA("");
      return;
    }

    if (!selectedPartyA || !partiesA.includes(selectedPartyA)) {
      const storedSelection = readElectionSelectionContext(areaId);
      setSelectedPartyA(
        pickValidChoice(
          partiesA,
          [getStoredChoiceForElection(storedSelection, "appPartyAByElection", electionIdA)],
          partiesA[0]
        )
      );
    }
  }, [areaId, electionIdA, partiesA, selectedPartyA]);

  useEffect(() => {
    // When election changes, parties list changes. Ensure the selected party is always valid.
    if (!partiesB || partiesB.length === 0) {
      if (selectedPartyB) setSelectedPartyB("");
      return;
    }

    if (!selectedPartyB || !partiesB.includes(selectedPartyB)) {
      const storedSelection = readElectionSelectionContext(areaId);
      setSelectedPartyB(
        pickValidChoice(
          partiesB,
          [getStoredChoiceForElection(storedSelection, "appPartyBByElection", electionIdB)],
          partiesB[0]
        )
      );
    }
  }, [areaId, electionIdB, partiesB, selectedPartyB]);

  // GeoJSON per map
  const districtsGeoJsonA = dataA.districtsGeoJson;
  const districtsGeoJsonB = dataB.districtsGeoJson;

  const maybeUpdateSharedBoundsFromLayer = useCallback((layer) => {
    try {
      if (!layer || typeof layer.getBounds !== "function") return;
      const b = layer.getBounds();
      if (!b || typeof b.isValid !== "function" || !b.isValid()) return;
      const sw = b.getSouthWest();
      const ne = b.getNorthEast();
      if (!sw || !ne) return;

      const boundsLike = [
        [sw.lat, sw.lng],
        [ne.lat, ne.lng],
      ];
      const sig = `${sw.lat.toFixed(6)},${sw.lng.toFixed(6)},${ne.lat.toFixed(6)},${ne.lng.toFixed(6)}`;
      if (sig === sharedBoundsSigRef.current) return;

      sharedBoundsSigRef.current = sig;
      sharedBoundsRef.current = boundsLike;
      setSharedBoundsSig(sig);
    } catch (_) {
      // ignore
    }
  }, []);

  // Apply the same explicit initial/area/reset fitBounds to both maps, without propagating via view sync.
  useEffect(() => {
    if (!fitRequestKey) return;
    if (!sharedBoundsSig) return;
    const bounds = sharedBoundsRef.current;
    if (!bounds) return;

    const padding = [20, 20];
    const maxZoom = 14;
    const requestKey = fitRequestKey;

    const apply = (which, map, ready) => {
      if (!ready || !map) return;
      if (lastFitAppliedRef.current[which] === requestKey) return;
      lastFitAppliedRef.current[which] = requestKey;
      const fit = () => {
        try {
          withProgrammaticMove(map, () => {
            if (typeof map.invalidateSize === "function") map.invalidateSize(false);
            map.fitBounds(bounds, { padding, maxZoom });
          });
        } catch (_) {}
      };

      // Defer and retry briefly so Leaflet has committed size/layout before the first analytical viewport.
      setTimeout(fit, 0);
      setTimeout(fit, 80);
      setTimeout(fit, 240);
    };

    apply("A", mapARef.current, mapAReady);
    apply("B", mapBRef.current, mapBReady);
  }, [fitRequestKey, mapAReady, mapBReady, sharedBoundsSig, withProgrammaticMove]);

  const valuesPctA = useMemo(() => {
    const feats = districtsGeoJsonA?.features || [];
    const out = [];
    for (const f of feats) {
      const okrsek = f?.properties?.okrsek;
      if (okrsek == null) continue;
      const pairKey = getPairKeyA(okrsek, f?.properties?.area_csu_code);
      const list = resultsByPairKeyA.get(String(pairKey)) || [];
      const hit = list.find((x) => getResultDisplayName(x) === selectedPartyA);
      const pct = hit?.percentage;
      if (Number.isFinite(pct)) out.push(Number(pct));
    }
    return out;
  }, [districtsGeoJsonA, getPairKeyA, resultsByPairKeyA, selectedPartyA]);

  const valuesPctB = useMemo(() => {
    const feats = districtsGeoJsonB?.features || [];
    const out = [];
    for (const f of feats) {
      const okrsek = f?.properties?.okrsek;
      if (okrsek == null) continue;
      const pairKey = getPairKeyB(okrsek, f?.properties?.area_csu_code);
      const list = resultsByPairKeyB.get(String(pairKey)) || [];
      const hit = list.find((x) => getResultDisplayName(x) === selectedPartyB);
      const pct = hit?.percentage;
      if (Number.isFinite(pct)) out.push(Number(pct));
    }
    return out;
  }, [districtsGeoJsonB, getPairKeyB, resultsByPairKeyB, selectedPartyB]);

  const autoBinsA = useMemo(() => computeAutoBins(valuesPctA, FIXED_CLASS_COUNT), [valuesPctA]);
  const autoBinsB = useMemo(() => computeAutoBins(valuesPctB, FIXED_CLASS_COUNT), [valuesPctB]);
  const binsA = autoBinsA;
  const binsB = autoBinsB;

  function getPartyValueForOkrsek(resultsByOkrsek, okrsek, selectedParty) {
    const list = resultsByOkrsek.get(String(okrsek)) || [];
    const hit = list.find((x) => getResultDisplayName(x) === selectedParty);
    if (!hit) return { percentage: null, votes: null };
    return { percentage: hit.percentage, votes: hit.votes };
  }

  const fillColorForPctA = useCallback(
    (pct) => {
      const idx = getBinIndex(pct, binsA);
      if (idx < 0) return NO_DATA_FILL_COLOR;
      const palette = getPaletteSteps(baseColorA);
      return palette[Math.min(idx, palette.length - 1)] || palette[palette.length - 1];
    },
    [binsA, baseColorA]
  );

  const fillColorForPctB = useCallback(
    (pct) => {
      const idx = getBinIndex(pct, binsB);
      if (idx < 0) return NO_DATA_FILL_COLOR;
      const palette = getPaletteSteps(baseColorB);
      return palette[Math.min(idx, palette.length - 1)] || palette[palette.length - 1];
    },
    [binsB, baseColorB]
  );

  const getStandardStyleA = useCallback(
    (feature, { isHovered = false } = {}) => {
      const okrsek = feature?.properties?.okrsek;
      const pairKey = getPairKeyA(okrsek, feature?.properties?.area_csu_code);
      const { percentage } = getPartyValueForOkrsek(resultsByPairKeyA, pairKey, selectedPartyA);
      const isClicked = clickedOkrsek != null && String(pairKey) === String(clickedOkrsek);
      return getElectionMapFeatureStyle({
        fillColor: fillColorForPctA(percentage),
        noDataFillColor: NO_DATA_FILL_COLOR,
        isHovered,
        isSelected: isClicked,
        clearOnFocus: true,
      });
    },
    [getPairKeyA, resultsByPairKeyA, selectedPartyA, fillColorForPctA, clickedOkrsek]
  );

  const styleFeatureA = useCallback(
    (feature) => {
      const okrsek = feature?.properties?.okrsek;
      const pairKey = getPairKeyA(okrsek, feature?.properties?.area_csu_code);
      const isHovered = hoveredOkrsek != null && String(pairKey) === String(hoveredOkrsek);
      return getStandardStyleA(feature, { isHovered });
    },
    [getPairKeyA, getStandardStyleA, hoveredOkrsek]
  );

  const getStandardStyleB = useCallback(
    (feature, { isHovered = false } = {}) => {
      const okrsek = feature?.properties?.okrsek;
      const pairKey = getPairKeyB(okrsek, feature?.properties?.area_csu_code);
      const { percentage } = getPartyValueForOkrsek(resultsByPairKeyB, pairKey, selectedPartyB);
      const isClicked = clickedOkrsek != null && String(pairKey) === String(clickedOkrsek);
      return getElectionMapFeatureStyle({
        fillColor: fillColorForPctB(percentage),
        noDataFillColor: NO_DATA_FILL_COLOR,
        isHovered,
        isSelected: isClicked,
        clearOnFocus: true,
      });
    },
    [getPairKeyB, resultsByPairKeyB, selectedPartyB, fillColorForPctB, clickedOkrsek]
  );

  const styleFeatureB = useCallback(
    (feature) => {
      const okrsek = feature?.properties?.okrsek;
      const pairKey = getPairKeyB(okrsek, feature?.properties?.area_csu_code);
      const isHovered = hoveredOkrsek != null && String(pairKey) === String(hoveredOkrsek);
      return getStandardStyleB(feature, { isHovered });
    },
    [getPairKeyB, getStandardStyleB, hoveredOkrsek]
  );

  function setLayerHoverState(layer, baseStyleFn, isHovered) {
    if (!layer || typeof layer.setStyle !== "function") return;
    const feat = layer.feature;
    if (!feat) return;

    if (isHovered) {
      layer.setStyle(baseStyleFn(feat, { isHovered: true }));
      try {
        layer.bringToFront && layer.bringToFront();
      } catch (_) {}
    } else {
      // reset base style
      try {
        layer.setStyle(baseStyleFn(feat));
      } catch (_) {}
    }
  }

  // Sync hover highlight + tooltips between both maps (each map shows its own data)
  useEffect(() => {
    const hovered = hoveredOkrsek ? String(hoveredOkrsek) : null;
    const prev = prevHoveredRef.current ? String(prevHoveredRef.current) : null;

    const makeHtmlA = (okrsek) => {
      const { percentage, votes } = getPartyValueForOkrsek(resultsByPairKeyA, okrsek, selectedPartyA);
      const st = getStatsForOkrsek(statsByPairKeyA, okrsek, electionIdA);
      const displayPrecinct = displayPrecinctByPairKeyA.get(String(okrsek)) || okrsek;

      const pctText = percentage == null ? "–" : `${percentage}%`;
      const votesText = votes == null ? "–" : formatInt(votes);

      const turnoutText = st?.turnoutPct != null ? formatPct(st.turnoutPct) : "–";
      const precinctLabel = getPrecinctLabel(displayPrecinct, electionIdA, comparableSenAreaCsuCodeA);
      const safePrecinctLabel = escapeHtml(precinctLabel);
      const safeParty = escapeHtml(selectedPartyA || "Vybraná strana");
      const safePctText = escapeHtml(pctText);
      const safeVotesText = escapeHtml(votesText);
      const safeTurnoutText = escapeHtml(turnoutText);
      return `
        <div class="tt-kicker">${safePrecinctLabel}</div>
        <div class="tt-party">${safeParty}</div>
        <div class="tt-value">${safePctText}</div>
        <div class="tt-meta">Hlasy: ${safeVotesText}</div>
        <div class="tt-meta">Účast: ${safeTurnoutText}</div>
      `;
    };

    const makeHtmlB = (okrsek) => {
      const { percentage, votes } = getPartyValueForOkrsek(resultsByPairKeyB, okrsek, selectedPartyB);
      const st = getStatsForOkrsek(statsByPairKeyB, okrsek, electionIdB);
      const displayPrecinct = displayPrecinctByPairKeyB.get(String(okrsek)) || okrsek;

      const pctText = percentage == null ? "–" : `${percentage}%`;
      const votesText = votes == null ? "–" : formatInt(votes);

      const turnoutText = st?.turnoutPct != null ? formatPct(st.turnoutPct) : "–";
      const precinctLabel = getPrecinctLabel(displayPrecinct, electionIdB, comparableSenAreaCsuCodeB);
      const safePrecinctLabel = escapeHtml(precinctLabel);
      const safeParty = escapeHtml(selectedPartyB || "Vybraná strana");
      const safePctText = escapeHtml(pctText);
      const safeVotesText = escapeHtml(votesText);
      const safeTurnoutText = escapeHtml(turnoutText);
      return `
        <div class="tt-kicker">${safePrecinctLabel}</div>
        <div class="tt-party">${safeParty}</div>
        <div class="tt-value">${safePctText}</div>
        <div class="tt-meta">Hlasy: ${safeVotesText}</div>
        <div class="tt-meta">Účast: ${safeTurnoutText}</div>
      `;
    };

    // reset previous hover (only the previously hovered okrsek)
    if (prev && prev !== hovered) {
      const laPrev = layerIndexARef.current.get(prev);
      const lbPrev = layerIndexBRef.current.get(prev);
      setLayerHoverState(laPrev, styleFeatureA, false);
      setLayerHoverState(lbPrev, styleFeatureB, false);
    }

    // apply current hover
    if (hovered) {
      const la = layerIndexARef.current.get(hovered);
      const lb = layerIndexBRef.current.get(hovered);

      setLayerHoverState(la, styleFeatureA, true);
      setLayerHoverState(lb, styleFeatureB, true);

      const latlng =
        lastHoverLatLngRef.current ||
        (la && typeof la.getBounds === "function" ? la.getBounds().getCenter() : null) ||
        (lb && typeof lb.getBounds === "function" ? lb.getBounds().getCenter() : null) ||
        null;

      if (la) {
        showHoverTooltip(mapARef.current, hoverTooltipARef, latlng, makeHtmlA(hovered));
      } else {
        hideHoverTooltip(mapARef.current, hoverTooltipARef);
      }

      if (lb) {
        showHoverTooltip(mapBRef.current, hoverTooltipBRef, latlng, makeHtmlB(hovered));
      } else {
        hideHoverTooltip(mapBRef.current, hoverTooltipBRef);
      }
    } else {
      hideHoverTooltip(mapARef.current, hoverTooltipARef);
      hideHoverTooltip(mapBRef.current, hoverTooltipBRef);
      lastHoverLatLngRef.current = null;
    }

    prevHoveredRef.current = hovered;
  }, [
    hoveredOkrsek,
    resultsByPairKeyA,
    resultsByPairKeyB,
    selectedPartyA,
    selectedPartyB,
    styleFeatureA,
    styleFeatureB,
    statsByPairKeyA,
    statsByPairKeyB,
    electionIdA,
    electionIdB,
    comparableSenAreaCsuCodeA,
    comparableSenAreaCsuCodeB,
    displayPrecinctByPairKeyA,
    displayPrecinctByPairKeyB,
    hideHoverTooltip,
    showHoverTooltip,
  ]);

  // If elections change, clear hover/click to avoid stale layer references
  useEffect(() => {
    setHoveredOkrsek(null);
    setClickedOkrsek(null);
    prevHoveredRef.current = null;
    lastHoverLatLngRef.current = null;
    // Remove any active hover tooltips (map-level instances).
    hideHoverTooltip(mapARef.current, hoverTooltipARef);
    hideHoverTooltip(mapBRef.current, hoverTooltipBRef);
    if (hoverClearTimerRef.current) {
      clearTimeout(hoverClearTimerRef.current);
      hoverClearTimerRef.current = null;
    }
    // Only clear the index for the map whose election changed.
    // If we clear both, the other map might not rerender (and thus won't repopulate),
    // breaking hover/click sync across maps.
    if (
      prevElectionIdARef.current !== electionIdA ||
      prevPairingSignatureARef.current !== pairingSignatureA
    ) {
      layerIndexARef.current = new Map();
    }
    if (
      prevElectionIdBRef.current !== electionIdB ||
      prevPairingSignatureBRef.current !== pairingSignatureB
    ) {
      layerIndexBRef.current = new Map();
    }
    prevElectionIdARef.current = electionIdA;
    prevElectionIdBRef.current = electionIdB;
    prevPairingSignatureARef.current = pairingSignatureA;
    prevPairingSignatureBRef.current = pairingSignatureB;
  }, [electionIdA, electionIdB, hideHoverTooltip, pairingSignatureA, pairingSignatureB]);

  const clearHoverAndTooltips = useCallback(() => {
    setHoveredOkrsek(null);
    if (hoverClearTimerRef.current) {
      clearTimeout(hoverClearTimerRef.current);
      hoverClearTimerRef.current = null;
    }

    const prev = prevHoveredRef.current ? String(prevHoveredRef.current) : null;
    if (prev) {
      const laPrev = layerIndexARef.current.get(prev);
      const lbPrev = layerIndexBRef.current.get(prev);
      setLayerHoverState(laPrev, styleFeatureA, false);
      setLayerHoverState(lbPrev, styleFeatureB, false);
    }

    // Clean up map-level hover tooltips.
    hideHoverTooltip(mapARef.current, hoverTooltipARef);
    hideHoverTooltip(mapBRef.current, hoverTooltipBRef);
    lastHoverLatLngRef.current = null;

    prevHoveredRef.current = null;
  }, [hideHoverTooltip, styleFeatureA, styleFeatureB]);

  const onEachFeatureA = useCallback(
    (feature, layer) => {
      const okrsek = feature?.properties?.okrsek;
      const pairKey = getPairKeyA(okrsek, feature?.properties?.area_csu_code);
      // index the layer by okrsek for fast hover syncing
      if (pairKey) {
        layerIndexARef.current.set(String(pairKey), layer);
      }
      layer.on({
        mouseover: (e) => {
          // cancel pending clear (moving fast between maps)
          if (hoverClearTimerRef.current) {
            clearTimeout(hoverClearTimerRef.current);
            hoverClearTimerRef.current = null;
          }
          lastHoverLatLngRef.current = e?.latlng || null;
          setHoveredOkrsek(pairKey);
        },
        mouseout: () => {
          // small delay prevents flicker when moving from map A -> map B (mouseout fires first)
          if (hoverClearTimerRef.current) clearTimeout(hoverClearTimerRef.current);
          const current = String(pairKey);
          hoverClearTimerRef.current = setTimeout(() => {
            // only clear if we are still hovering the same okrsek
            setHoveredOkrsek((prev) => (prev && String(prev) === current ? null : prev));
            hoverClearTimerRef.current = null;
          }, 40);
        },
        click: () => {
          setClickedOkrsek(pairKey);
        },
        mousedown: () => {
          userGestureARef.current = true;
          clearHoverAndTooltips();
        },
        touchstart: () => {
          userGestureARef.current = true;
          clearHoverAndTooltips();
        },
      });
    },
    [clearHoverAndTooltips, getPairKeyA]
  );

  const onEachFeatureB = useCallback(
    (feature, layer) => {
      const okrsek = feature?.properties?.okrsek;
      const pairKey = getPairKeyB(okrsek, feature?.properties?.area_csu_code);
      if (pairKey) {
        layerIndexBRef.current.set(String(pairKey), layer);
      }
      layer.on({
        mouseover: (e) => {
          if (hoverClearTimerRef.current) {
            clearTimeout(hoverClearTimerRef.current);
            hoverClearTimerRef.current = null;
          }
          lastHoverLatLngRef.current = e?.latlng || null;
          setHoveredOkrsek(pairKey);
        },
        mouseout: () => {
          if (hoverClearTimerRef.current) clearTimeout(hoverClearTimerRef.current);
          const current = String(pairKey);
          hoverClearTimerRef.current = setTimeout(() => {
            setHoveredOkrsek((prev) => (prev && String(prev) === current ? null : prev));
            hoverClearTimerRef.current = null;
          }, 40);
        },
        click: () => {
          setClickedOkrsek(pairKey);
        },
        mousedown: () => {
          userGestureBRef.current = true;
          clearHoverAndTooltips();
        },
        touchstart: () => {
          userGestureBRef.current = true;
          clearHoverAndTooltips();
        },
      });
    },
    [clearHoverAndTooltips, getPairKeyB]
  );

  // Legend items
  const legendItemsA = useMemo(() => {
    const items = [];
    for (let i = 0; i < binsA.length - 1; i++) {
      const from = binsA[i];
      const to = binsA[i + 1];
      const mid = from + (to - from) / 2;
      items.push({ label: formatBinRange(from, to), color: fillColorForPctA(mid) });
    }
    return items;
  }, [binsA, fillColorForPctA]);

  const legendItemsB = useMemo(() => {
    const items = [];
    for (let i = 0; i < binsB.length - 1; i++) {
      const from = binsB[i];
      const to = binsB[i + 1];
      const mid = from + (to - from) / 2;
      items.push({ label: formatBinRange(from, to), color: fillColorForPctB(mid) });
    }
    return items;
  }, [binsB, fillColorForPctB]);

  const clickedResultsA = useMemo(() => {
    if (!clickedOkrsek) return [];
    return resultsByPairKeyA.get(String(clickedOkrsek)) || [];
  }, [clickedOkrsek, resultsByPairKeyA]);

  const clickedResultsB = useMemo(() => {
    if (!clickedOkrsek) return [];
    return resultsByPairKeyB.get(String(clickedOkrsek)) || [];
  }, [clickedOkrsek, resultsByPairKeyB]);

  const clickedStatsA = useMemo(() => {
    if (!clickedOkrsek) return null;
    return getStatsForOkrsek(statsByPairKeyA, clickedOkrsek, electionIdA);
  }, [clickedOkrsek, statsByPairKeyA, electionIdA]);

  const clickedStatsB = useMemo(() => {
    if (!clickedOkrsek) return null;
    return getStatsForOkrsek(statsByPairKeyB, clickedOkrsek, electionIdB);
  }, [clickedOkrsek, statsByPairKeyB, electionIdB]);

  const clickedDisplayPrecinctA = useMemo(() => {
    if (!clickedOkrsek) return null;
    return displayPrecinctByPairKeyA.get(String(clickedOkrsek)) || String(clickedOkrsek);
  }, [clickedOkrsek, displayPrecinctByPairKeyA]);

  const clickedDisplayPrecinctB = useMemo(() => {
    if (!clickedOkrsek) return null;
    return displayPrecinctByPairKeyB.get(String(clickedOkrsek)) || String(clickedOkrsek);
  }, [clickedOkrsek, displayPrecinctByPairKeyB]);

  const binsKeyA = autoBinsA.join(",");
  const binsKeyB = autoBinsB.join(",");
  const mapKeyA = `A-${electionIdA}-${selectedPartyA}-${baseColorA}-${binsKeyA}-${pairingSignatureA}`;
  const mapKeyB = `B-${electionIdB}-${selectedPartyB}-${baseColorB}-${binsKeyB}-${pairingSignatureB}`;
  const electionLabelA = getElectionLabel(elections.find((election) => String(election.id) === String(electionIdA)) || { id: electionIdA });
  const electionLabelB = getElectionLabel(elections.find((election) => String(election.id) === String(electionIdB)) || { id: electionIdB });

  const buildAppReportSnapshot = useCallback(
    (panel) => {
      const isPanelA = panel === "A";
      const selectedParty = isPanelA ? selectedPartyA : selectedPartyB;
      const partyKey = findResultIdentityByDisplayName(isPanelA ? dataA.results : dataB.results, selectedParty);
      return {
        kind: REPORT_ITEM_TYPES.APP_MAP,
        sourceModule: REPORT_SOURCE_MODULES.APP,
        panel,
        mapMode: "choropleth",
        areaId,
        electionId: isPanelA ? electionIdA : electionIdB,
        electionLabel: isPanelA ? electionLabelA : electionLabelB,
        partyKey,
        partyCode: partyKey,
        partyLabel: selectedParty,
        colorState: {
          baseColor: isPanelA ? baseColorA : baseColorB,
          classCount: FIXED_CLASS_COUNT,
          bins: isPanelA ? binsA : binsB,
        },
        legend: mapLegendToSnapshot(isPanelA ? legendItemsA : legendItemsB),
        viewport: captureLeafletViewport(isPanelA ? mapARef.current : mapBRef.current) || initialViewport,
        capturedAt: new Date().toISOString(),
      };
    },
    [
      areaId,
      baseColorA,
      baseColorB,
      binsA,
      binsB,
      dataA.results,
      dataB.results,
      electionIdA,
      electionIdB,
      electionLabelA,
      electionLabelB,
      initialViewport,
      legendItemsA,
      legendItemsB,
      selectedPartyA,
      selectedPartyB,
    ]
  );

  // --- Sync pan/zoom between Map A and Map B ---
  const pendingSyncRef = useRef(null);
  const pendingSyncFrameRef = useRef(null);

  const syncToOtherMap = useCallback((source, center, zoom) => {
    writeStoredViewport("app", areaId, { center, zoom });
    pendingSyncRef.current = { source, center, zoom };

    // Tiny throttle: coalesce rapid end-events (trackpad storms) into one sync.
    if (pendingSyncFrameRef.current != null) return;
    const schedule =
      typeof requestAnimationFrame === "function"
        ? requestAnimationFrame
        : (cb) => setTimeout(cb, 50);

    pendingSyncFrameRef.current = schedule(() => {
      pendingSyncFrameRef.current = null;

      const p = pendingSyncRef.current;
      if (!p) return;

      const mapA = mapARef.current;
      const mapB = mapBRef.current;
      if (!mapAReady || !mapBReady || !mapA || !mapB) return;

      try {
        const target = p.source === "A" ? mapB : mapA;
        const targetZoom = target.getZoom();
        const targetCenter = target.getCenter();
        if (
          targetZoom === p.zoom &&
          Math.abs(targetCenter.lat - p.center.lat) < 1e-6 &&
          Math.abs(targetCenter.lng - p.center.lng) < 1e-6
        ) {
          return; // no-op: avoids unnecessary programmatic guard and missing end-events on some platforms
        }
        withProgrammaticMove(target, () => {
          target.setView(p.center, p.zoom, { animate: false });
        });
      } catch (_) {
        // ignore
      }
    });
  }, [areaId, mapAReady, mapBReady, withProgrammaticMove]);

  const resetCompareViewport = useCallback(() => {
    setHoveredOkrsek(null);
    setClickedOkrsek(null);
    prevHoveredRef.current = null;
    lastHoverLatLngRef.current = null;
    hideHoverTooltip(mapARef.current, hoverTooltipARef);
    hideHoverTooltip(mapBRef.current, hoverTooltipBRef);
    clearStoredViewport("app", areaId);
    lastFitAppliedRef.current = { A: "", B: "" };
    setFitRequestKey(makeAutoFitRequestKey("app", areaId, "reset"));
  }, [areaId, hideHoverTooltip]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mql = window.matchMedia(`(min-width: ${WIDE_LAYOUT_BREAKPOINT}px)`);
    const onChange = (e) => setIsWideLayout(e.matches);
    setIsWideLayout(mql.matches);
    if (typeof mql.addEventListener === "function") {
      mql.addEventListener("change", onChange);
      return () => mql.removeEventListener("change", onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      try {
        mapARef.current?.invalidateSize?.();
      } catch (_) {}
      try {
        mapBRef.current?.invalidateSize?.();
      } catch (_) {}
    }, 80);
    return () => clearTimeout(t);
  }, [isWideLayout]);

  const handleMapMouseLeave = useCallback(() => {
    setHoveredOkrsek(null);
    if (hoverClearTimerRef.current) {
      clearTimeout(hoverClearTimerRef.current);
      hoverClearTimerRef.current = null;
    }
  }, []);

  const mapPaletteA = getPaletteSteps(baseColorA);
  const mapPaletteB = getPaletteSteps(baseColorB);

  return (
    <>
      <style>{`
        .compare-layout {
          width: 100%;
          padding: 8px 0 16px;
          background: ${APP_PAGE_BACKGROUND};
          overflow-x: hidden;
          font-family: ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji";
          --compare-map-height: clamp(420px, calc(var(--app-shell-available-height, 100vh) - 150px), 720px);
        }
        .compare-grid {
          display: grid;
          column-gap: var(--app-shell-major-column-gap, 18px);
          row-gap: var(--app-shell-major-gap, 12px);
          grid-template-columns: repeat(2, minmax(0, 1fr));
          align-items: start;
        }
        .compare-grid, .compare-grid * {
          box-sizing: border-box;
        }
        .compare-card {
          min-width: 0;
          border: 1px solid #dbe4f0;
          border-radius: 22px;
          background: rgba(255, 255, 255, 0.98);
          box-shadow: 0 18px 40px rgba(15, 23, 42, 0.07);
        }
        .compare-controls-card,
        .compare-pinned-card {
          padding: 13px;
        }
        .compare-map-card {
          padding: 12px;
          display: grid;
          gap: 6px;
        }
        .compare-panel-meta {
          display: inline-flex;
          align-items: center;
          border-radius: 999px;
          padding: 6px 10px;
          font-size: 12px;
          font-weight: 700;
          min-width: 0;
          max-width: 100%;
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
          border: 1px solid transparent;
        }
        .compare-controls-body {
          display: grid;
          gap: 10px;
          grid-template-columns: repeat(2, minmax(0, 1fr));
          align-items: stretch;
        }
        .compare-controls-column {
          display: grid;
          gap: 8px;
          align-content: start;
        }
        .compare-control-stack {
          display: grid;
          gap: 5px;
          min-width: 0;
        }
        .compare-control-label {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #64748b;
        }
        .compare-select {
          width: 100%;
          min-width: 0;
          border: 1px solid #d7e1ee;
          border-radius: 10px;
          background: #f8fafc;
          color: #0f172a;
          min-height: 34px;
          padding: 7px 10px;
          font-size: 13px;
          font-weight: 600;
        }
        .compare-color-row {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr);
          gap: 8px;
          align-items: center;
        }
        .compare-color-preview {
          display: inline-grid;
          grid-template-columns: repeat(4, 10px);
          gap: 3px;
          padding: 5px;
          border: 1px solid #d7e1ee;
          border-radius: 999px;
          background: #ffffff;
        }
        .compare-color-preview-step {
          width: 10px;
          height: 16px;
          border-radius: 999px;
          display: block;
        }
        .compare-legend-block {
          display: grid;
          grid-template-rows: auto 1fr;
          gap: 8px;
          min-width: 0;
          align-content: start;
        }
        .compare-legend-heading {
          display: flex;
          align-items: baseline;
          gap: 10px;
        }
        .compare-legend-list {
          display: grid;
          align-content: space-between;
          gap: 10px;
          border: 1px solid #dbe4f0;
          border-radius: 12px;
          background: linear-gradient(180deg, #fbfdff 0%, #ffffff 100%);
          padding: 11px 12px;
          min-height: 100%;
        }
        .compare-legend-row {
          display: grid;
          grid-template-columns: auto minmax(0, 1fr);
          align-items: center;
          gap: 8px;
          min-width: 0;
          padding: 0;
          font-size: 12px;
          color: #334155;
        }
        .compare-legend-swatch {
          width: 18px;
          height: 12px;
          border-radius: 999px;
          border: 1px solid rgba(15, 23, 42, 0.18);
          flex: 0 0 auto;
        }
        .compare-map-frame {
          border: 1px solid #dbe4f0;
          border-radius: 18px;
          overflow: hidden;
          background: #eaf1f8;
        }
        .compare-map-toolbar {
          display: flex;
          justify-content: space-between;
          align-items: center;
          gap: 10px;
          min-height: 28px;
          padding: 0 2px;
        }
        .compare-map-panel-id {
          font-size: 12px;
          font-weight: 800;
          letter-spacing: 0.04em;
          color: #475569;
          white-space: nowrap;
        }
        .compare-map-status {
          display: grid;
          gap: 8px;
        }
        .compare-status-pill {
          display: inline-flex;
          width: fit-content;
          align-items: center;
          border-radius: 999px;
          padding: 7px 11px;
          background: #eff6ff;
          color: #1e40af;
          font-size: 12px;
          font-weight: 700;
        }
        .compare-error-list {
          display: grid;
          gap: 6px;
          padding: 12px 14px;
          border: 1px solid #fecaca;
          border-radius: 16px;
          background: #fef2f2;
          color: #991b1b;
          font-size: 13px;
        }
        .compare-pinned-empty {
          padding: 18px;
          border: 1px dashed #cbd5e1;
          border-radius: 16px;
          background: #f8fafc;
          color: #475569;
          font-size: 14px;
        }
        .compare-pinned-summary {
          display: grid;
          gap: 10px;
        }
        .compare-pinned-topline {
          display: flex;
          align-items: center;
        }
        .compare-pinned-title {
          font-size: 1.05rem;
          font-weight: 800;
          line-height: 1.2;
          color: #0f172a;
        }
        .compare-selected-party-card {
          display: grid;
          gap: 3px;
          padding: 12px 13px;
          border: 1px solid #dbe4f0;
          border-radius: 18px;
          background: linear-gradient(180deg, #f8fbff 0%, #eff6ff 100%);
        }
        .compare-selected-party-label {
          font-size: 13px;
          font-weight: 700;
          color: #334155;
        }
        .compare-selected-party-stats-line {
          display: flex;
          align-items: baseline;
          gap: 12px;
          flex-wrap: wrap;
        }
        .compare-selected-party-value {
          font-size: clamp(1.45rem, 3.2vw, 1.95rem);
          line-height: 1;
          font-weight: 900;
          letter-spacing: -0.04em;
        }
        .compare-selected-party-meta {
          font-size: 12px;
          color: #475569;
        }
        .compare-stat-grid {
          display: grid;
          grid-template-columns: repeat(3, minmax(0, 1fr));
          gap: 8px;
        }
        .compare-stat-box {
          display: grid;
          gap: 4px;
          padding: 11px;
          border: 1px solid #e2e8f0;
          border-radius: 16px;
          background: #f8fafc;
        }
        .compare-stat-label {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #64748b;
        }
        .compare-results-table {
          display: grid;
          gap: 6px;
          margin-top: 10px;
        }
        .compare-results-head,
        .compare-results-row {
          display: grid;
          grid-template-columns: minmax(0, 1fr) minmax(78px, auto) minmax(62px, auto);
          column-gap: 10px;
          align-items: center;
        }
        .compare-results-head {
          padding: 0 10px;
        }
        .compare-results-header {
          font-size: 11px;
          font-weight: 800;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #64748b;
        }
        .compare-results-header-right {
          text-align: right;
        }
        .compare-results-row {
          min-width: 0;
          padding: 8px 10px;
          border-left: 4px solid transparent;
          border-radius: 12px;
          background: #f8fafc;
          font-size: 13px;
          color: #0f172a;
        }
        .compare-results-name {
          display: block;
          min-width: 0;
          overflow-wrap: anywhere;
          line-height: 1.25;
        }
        .compare-results-value {
          font-size: 13px;
          text-align: right;
          color: #0f172a;
          font-variant-numeric: tabular-nums;
        }
        .compare-results-row.is-selected {
          font-weight: 800;
        }
        .leaflet-container:focus,
        .leaflet-interactive:focus,
        .leaflet-interactive {
          outline: none;
        }
        .leaflet-tooltip.hover-tooltip {
          background: #ffffff;
          border: 1px solid #dbe4f0;
          border-radius: 8px;
          padding: 8px 10px;
          box-shadow: 0 8px 18px rgba(15, 23, 42, 0.14);
          font-size: 12px;
          color: #111827;
          width: auto;
          min-width: 168px;
          max-width: 220px;
          box-sizing: border-box;
          display: block;
          white-space: normal;
          overflow-wrap: anywhere;
          word-break: normal;
          pointer-events: none;
        }
        .leaflet-tooltip.hover-tooltip:before {
          display: none;
        }
        .leaflet-tooltip.hover-tooltip .tt-kicker {
          font-weight: 700;
          color: #111827;
          margin-bottom: 3px;
        }
        .leaflet-tooltip.hover-tooltip .tt-party {
          font-size: 12px;
          font-weight: 700;
          color: #64748b;
          margin-bottom: 2px;
        }
        .leaflet-tooltip.hover-tooltip .tt-value {
          font-size: 1rem;
          line-height: 1.1;
          font-weight: 800;
          color: #0f172a;
          margin-bottom: 4px;
        }
        .leaflet-tooltip.hover-tooltip .tt-meta {
          color: #475569;
          line-height: 1.3;
        }
        @media (max-width: 1099px) {
          .compare-grid {
            grid-template-columns: 1fr;
          }
        }
        @media (max-width: 720px) {
          .compare-layout {
            padding-bottom: 14px;
          }
          .compare-controls-body,
          .compare-stat-grid {
            grid-template-columns: 1fr;
          }
          .compare-color-row {
            grid-template-columns: 1fr;
            justify-items: start;
          }
        }
      `}</style>
      <div className="compare-layout">
        {boundaryComparisonBlocked ? (
          <p role="status" style={{ padding: "12px 16px", background: "#fff7ed", color: "#7c2d12", borderRadius: 12 }}>
            {BOUNDARY_CHANGE_NOTICE}
          </p>
        ) : null}
        <div className="compare-grid">
          <MapControlCard
            title="Mapa A"
            elections={elections}
            electionId={electionIdA}
            setElectionId={setElectionIdA}
            parties={partiesA}
            selectedParty={selectedPartyA}
            setSelectedParty={setSelectedPartyA}
            baseColor={baseColorA}
            setBaseColor={setBaseColorA}
            legendItems={legendItemsA}
          />

          <MapControlCard
            title="Mapa B"
            elections={elections}
            electionId={electionIdB}
            setElectionId={setElectionIdB}
            parties={partiesB}
            selectedParty={selectedPartyB}
            setSelectedParty={setSelectedPartyB}
            baseColor={baseColorB}
            setBaseColor={setBaseColorB}
            legendItems={legendItemsB}
          />
          <section className="compare-card compare-map-card">
            <div className="compare-map-toolbar">
              <div className="compare-map-panel-id">Mapa A</div>
              <div
                className="compare-panel-meta"
                style={{
                  background: mixHex(mapPaletteA[0], "#ffffff", 0.16),
                  borderColor: mixHex(mapPaletteA[2], "#ffffff", 0.4),
                  color: mapPaletteA[3],
                }}
              >
                {selectedPartyA || "Bez výběru"}
              </div>
              {enableReports ? (
                <AddToReportButton
                  type={REPORT_ITEM_TYPES.APP_MAP}
                  sourceModule={REPORT_SOURCE_MODULES.APP}
                  defaultOrientation={REPORT_ORIENTATIONS.LANDSCAPE}
                  defaultTitle={buildDefaultReportTitle([electionLabelA, selectedPartyA || "Mapa A", "Mapa"])}
                  buildSnapshot={() => buildAppReportSnapshot("A")}
                />
              ) : null}
            </div>
            <div className="compare-map-frame">
              <MapPane
                mapKey={mapKeyA}
                districtsGeoJson={districtsGeoJsonA}
                styleFeature={styleFeatureA}
                onEachFeature={onEachFeatureA}
                mapStandard={ELECTION_MAP_STANDARD_V1_KEY}
                tileAttribution={CARTO_ATTR}
                height="var(--compare-map-height)"
                scrollWheelZoom={false}
                onMapReady={(m) => {
                  mapARef.current = m;
                  setMapAReady(true);
                }}
                onViewChange={(center, zoom) => syncToOtherMap("A", center, zoom)}
                onGeoJsonReady={(layer) => {
                  maybeUpdateSharedBoundsFromLayer(layer);
                }}
                onMouseLeave={handleMapMouseLeave}
                onInteractionStart={clearHoverAndTooltips}
                userGestureRef={userGestureARef}
                initialView={initialViewport}
                onResetView={resetCompareViewport}
              />
            </div>
            <MapStatusCard
              districtsError={dataA.districtsError}
              resultsError={dataA.resultsError}
              statsError={dataA.statsError}
              isLoadingDistricts={dataA.isLoadingDistricts}
              isLoadingResults={dataA.isLoadingResults}
              isLoadingStats={dataA.isLoadingStats}
            />
          </section>

          <section className="compare-card compare-map-card">
            <div className="compare-map-toolbar">
              <div className="compare-map-panel-id">Mapa B</div>
              <div
                className="compare-panel-meta"
                style={{
                  background: mixHex(mapPaletteB[0], "#ffffff", 0.16),
                  borderColor: mixHex(mapPaletteB[2], "#ffffff", 0.4),
                  color: mapPaletteB[3],
                }}
              >
                {selectedPartyB || "Bez výběru"}
              </div>
              {enableReports ? (
                <AddToReportButton
                  type={REPORT_ITEM_TYPES.APP_MAP}
                  sourceModule={REPORT_SOURCE_MODULES.APP}
                  defaultOrientation={REPORT_ORIENTATIONS.LANDSCAPE}
                  defaultTitle={buildDefaultReportTitle([electionLabelB, selectedPartyB || "Mapa B", "Mapa"])}
                  buildSnapshot={() => buildAppReportSnapshot("B")}
                />
              ) : null}
            </div>
            <div className="compare-map-frame">
              <MapPane
                mapKey={mapKeyB}
                districtsGeoJson={districtsGeoJsonB}
                styleFeature={styleFeatureB}
                onEachFeature={onEachFeatureB}
                mapStandard={ELECTION_MAP_STANDARD_V1_KEY}
                tileAttribution={CARTO_ATTR}
                height="var(--compare-map-height)"
                scrollWheelZoom={false}
                onMapReady={(m) => {
                  mapBRef.current = m;
                  setMapBReady(true);
                }}
                onViewChange={(center, zoom) => syncToOtherMap("B", center, zoom)}
                onGeoJsonReady={(layer) => {
                  maybeUpdateSharedBoundsFromLayer(layer);
                }}
                onMouseLeave={handleMapMouseLeave}
                onInteractionStart={clearHoverAndTooltips}
                userGestureRef={userGestureBRef}
                initialView={initialViewport}
                onResetView={resetCompareViewport}
              />
            </div>
            <MapStatusCard
              districtsError={dataB.districtsError}
              resultsError={dataB.resultsError}
              statsError={dataB.statsError}
              isLoadingDistricts={dataB.isLoadingDistricts}
              isLoadingResults={dataB.isLoadingResults}
              isLoadingStats={dataB.isLoadingStats}
            />
          </section>

          <ClickedPanel
            title="Mapa A"
            clickedOkrsek={boundaryComparisonBlocked && !displayPrecinctByPairKeyA.has(String(clickedOkrsek)) ? null : clickedOkrsek}
            clickedDisplayPrecinct={clickedDisplayPrecinctA}
            clickedResults={clickedResultsA}
            clickedStats={clickedStatsA}
            comparableSenAreaCsuCode={comparableSenAreaCsuCodeA}
            selectedParty={selectedPartyA}
            baseColor={baseColorA}
          />

          <ClickedPanel
            title="Mapa B"
            clickedOkrsek={boundaryComparisonBlocked && !displayPrecinctByPairKeyB.has(String(clickedOkrsek)) ? null : clickedOkrsek}
            clickedDisplayPrecinct={clickedDisplayPrecinctB}
            clickedResults={clickedResultsB}
            clickedStats={clickedStatsB}
            comparableSenAreaCsuCode={comparableSenAreaCsuCodeB}
            selectedParty={selectedPartyB}
            baseColor={baseColorB}
          />
        </div>
      </div>
    </>
  );
}
